(ns app.social-scheduling.page
  "Generic scheduling UI for Instagram Stories, Reels, TikTok posts,
   YouTube Shorts, and the bundle.social platforms (see
   `app.bundle-social.platforms`)."
  (:require [app.bundle-social.page :as social-accounts-page]
            [app.bundle-social.platforms :as social-platforms]
            [app.bundle-social.text :as text]
            [clojure.string :as str]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.caption-limits :as caption-limits]
            [storrito.date.datetime-local :as datetime-local]
            [storrito.date.format :as f]
            [ui.app-frame :as app-frame]
            [ui.icons :as icons]
            [ui.social-account.connect-modal :as connect-modal]))

(def platforms
  "The destination kinds: Storrito's own platforms, then one per post
   type of every bundle.social platform (`:facebook-reel`,
   `:facebook-story`, ...). See `offered-platforms` for the ones a
   user sees."
  (into [:instagram :reels :tiktok :youtube]
        (map :post-type/kind)
        (social-platforms/post-types)))

(defn- social-kind?
  "A destination kind of a bundle.social platform."
  [kind]
  (some? (social-platforms/post-type-by-kind kind)))

(defn offered-platforms
  "The destination kinds offered: all but the post types of an
   unlaunched bundle.social platform, which only show up once the
   organization connected an account of it (through
   `/ui/social-accounts?platform=<id>`, the internal test)."
  [result]
  (let [connected (set (map :social.account/platform (:social/accounts result)))]
    (filterv (fn [kind]
               (let [post-type (social-platforms/post-type-by-kind kind)]
                 (or (nil? post-type)
                     (:social-platform/launched?
                       (social-platforms/by-id (:social-platform/id post-type)))
                     (contains? connected (:social-platform/id post-type)))))
             platforms)))

(defn social-scheduling-query
  [state]
  (let [eid (::story-media-eid state)]
    (when eid
      {:query/kind :query/social-scheduling
       :query/refresh? true
       :query/data {:story-media/eid eid}})))

(defn- tiktok-creator-info-query
  [account-id]
  (when account-id
    {:query/kind :query/tiktok-creator-info
     :query/data {:tiktok.user/id account-id}}))

(defn- instagram-highlights-query
  [user-id]
  (when user-id
    {:query/kind :query/social-scheduling-highlights
     :query/data {:instagram.user/id user-id}}))

(def privacy-level-labels
  "TikTok's privacy levels and their labels, from the catalog's TikTok
   post type (`app.bundle-social-tiktok.platform`)."
  (into {}
        (comp (filter #(= :privacy (:option/key %)))
              (mapcat :option/choices)
              (map (juxt :choice/value :choice/label)))
        (social-platforms/options "TIKTOK" "POST")))

(defn- tiktok-creator-info-result
  "The answer of `:query/tiktok-creator-info` for an own account."
  [state account-id]
  (when-let [q (tiktok-creator-info-query account-id)]
    (query/get-result state q)))

(defn- tiktok-creator-info
  [state account-id]
  (when-let [q (tiktok-creator-info-query account-id)]
    (:tiktok/creator-info (query/get-result state q))))

(defn- selected-tiktok-privacy-level
  [state privacy-levels]
  (let [selected (::tiktok-privacy-level state)]
    (when (some #{selected} privacy-levels)
      selected)))

(defn- tiktok-privacy-level-selected?
  [state account-id]
  (boolean
    (selected-tiktok-privacy-level
      state
      (:privacy_level_options (tiktok-creator-info state account-id)))))

(defn- parse-float-value
  [value]
  (when (seq (str value))
    (let [n (js/parseFloat value)]
      (when-not (js/isNaN n)
        n))))

(defn- tiktok-video-cover-timestamp-ms
  [state]
  (when-let [seconds (parse-float-value (or (not-empty (::tiktok-video-cover-time-seconds state))
                                            "1"))]
    (max 0 (.round js/Math (* seconds 1000)))))

(defn- selected-date-ms
  [state]
  (datetime-local/date-ms (::schedule-date-str state)))

(defn- schedule-date-error
  [state]
  (when (seq (::schedule-date-str state))
    (if-let [ms (selected-date-ms state)]
      (when-not (datetime-local/current-minute-or-future? ms)
        "Date must not be in the past.")
      "Choose a valid date and time.")))

(defn- target-date-ms
  [state]
  (or (selected-date-ms state)
      (.getTime (js/Date.))))

(defn- same-minute-ms?
  [left-ms right-ms]
  (= (.floor js/Math (/ left-ms datetime-local/minute-ms))
     (.floor js/Math (/ right-ms datetime-local/minute-ms))))

(defn- date-value-ms
  [date]
  (cond
    (number? date)
    date

    (instance? js/Date date)
    (.getTime date)

    :else
    nil))

(defn- selected-destinations
  [state]
  (vec (or (::selected-destinations state) [])))

(defn- selected-platforms
  [state]
  (if (contains? state ::selected-destinations)
    (into #{} (map :destination/kind) (selected-destinations state))
    (or (::selected-platforms state) #{})))

(defn- platform-selected?
  [state platform]
  (contains? (selected-platforms state) platform))

(defn- selected-instagram-accounts
  [state]
  (if (contains? state ::selected-destinations)
    (filterv #(= :instagram (:destination/kind %))
             (selected-destinations state))
    (vec (or (::selected-instagram-accounts state) []))))

(defn toggle-fb-destination
  [selected user-id fb-linkage]
  (mapv (fn [entry]
          (if (and (= :instagram (:destination/kind entry))
                   (= (:instagram.user/id entry) user-id))
            (let [dest-id (str (:facebook-linkage/id fb-linkage))
                  currently-selected (and (:share-to-fb? entry)
                                          (= (:facebook-linkage/destination entry) dest-id))]
              (if currently-selected
                (dissoc (assoc entry :share-to-fb? false)
                        :facebook-linkage/destination
                        :facebook-linkage/type)
                (assoc entry
                       :share-to-fb? true
                       :facebook-linkage/destination dest-id
                       :facebook-linkage/type (:facebook-linkage/type fb-linkage))))
            entry))
        selected))

(defn- caption
  [state]
  (str/trim (or (::caption state) "")))

(defn- story-first-comment
  [state]
  (str/trim (or (::story-first-comment state) "")))

(defn- youtube-title
  [state result]
  (str/trim (or (::youtube-title state)
                (:story-media/name result)
                "")))

(defn- connected-instagram-users
  [result]
  (remove :instagram.account/not-connected
          (:instagram/users result)))

(defn- selected-ids
  [state store-key]
  (vec (or (get state store-key) [])))

(defn- selected-reels-ids
  [state]
  (if (contains? state ::selected-destinations)
    (mapv :instagram.user/id
          (filter #(= :reels (:destination/kind %))
                  (selected-destinations state)))
    (selected-ids state ::selected-reels-ids)))

(defn- selected-tiktok-ids
  [state]
  (if (contains? state ::selected-destinations)
    (mapv :tiktok.user/id
          (filter #(= :tiktok (:destination/kind %))
                  (selected-destinations state)))
    (selected-ids state ::selected-tiktok-ids)))

(defn- selected-youtube-ids
  [state]
  (if (contains? state ::selected-destinations)
    (mapv :youtube.user/id
          (filter #(= :youtube (:destination/kind %))
                  (selected-destinations state)))
    (selected-ids state ::selected-youtube-ids)))

(defn- selected-social-account-uuids
  [state kind]
  (mapv :social.account/uuid
        (filter #(= kind (:destination/kind %))
                (selected-destinations state))))

(defn- duration-too-long?
  [result]
  (let [duration (:story-media/duration result)]
    (and (number? duration)
         (> duration 180))))

(defn- social-media
  "`:video`, `:image`, `:pending` (a story media that is still
   rendering into what the post type takes) or nil: what a post of
   `kind` would publish, like `app.bundle-social.media/choose`. A video
   wins."
  [kind result]
  (let [media (:post-type/media (social-platforms/post-type-by-kind kind))]
    (cond
      (and (contains? media :video) (:story-media/video-blob-id result)) :video
      (and (contains? media :image) (:story-media/image-post? result)) :image
      (and (:story-media/rendering? result)
           (contains? media (:story-media/expected-media result))) :pending
      :else nil)))

(defn- social-video-duration
  "The length of the video a post of `kind` would publish: the rendered
   one's, or the expected one while it renders; nil for an image."
  [kind result]
  (case (social-media kind result)
    :video (:story-media/duration result)
    :pending (:story-media/expected-duration result)
    nil))

(defn- social-too-long?
  "The video is longer than the post type of `kind` allows."
  [kind result]
  (let [duration (social-video-duration kind result)
        limit (:post-type/max-duration-seconds (social-platforms/post-type-by-kind kind))]
    (and (number? duration)
         (number? limit)
         (> duration limit))))

(defn- social-too-short?
  "The video is shorter than the post type of `kind` allows."
  [kind result]
  (let [duration (social-video-duration kind result)
        limit (:post-type/min-duration-seconds (social-platforms/post-type-by-kind kind))]
    (and (number? duration)
         (number? limit)
         (< duration limit))))

(defn- social-too-short-text
  [kind]
  (let [post-type (social-platforms/post-type-by-kind kind)]
    (str (:post-type/group-label post-type) " must be at least "
         (:post-type/min-duration-seconds post-type) " seconds long.")))

(defn- social-too-long-text
  [kind]
  (let [post-type (social-platforms/post-type-by-kind kind)]
    (str (:post-type/group-label post-type) " can be at most "
         (let [seconds (:post-type/max-duration-seconds post-type)]
           (if (and (< 60 seconds) (zero? (mod seconds 60)))
             (let [minutes (quot seconds 60)]
               (if (= 1 minutes) "1 minute" (str minutes " minutes")))
             (str seconds " seconds")))
         " long.")))

(defn- instagram-allowed?
  [result]
  (or (:organization/positive-credit-balance? result)
      (:organization/legacy-customer? result)
      (pos? (or (:organization/trial-posts-remaining result) 0))))

(defn- paid-platform-allowed?
  [result]
  (or (:organization/positive-credit-balance? result)
      (:organization/legacy-customer? result)))

(defn- destination-key
  [kind id]
  (str (name kind) ":" id))

(def sticker-labels
  {:poll "Poll"
   :quiz "Quiz"
   :question "Question"
   :slider "Emoji Slider"
   :countdown "Countdown"})

(defn- unsupported-stickers-text
  [unsupported]
  (let [labels (keep sticker-labels unsupported)]
    (when (seq labels)
      (str "The design contains the "
           (str/join " and " labels)
           " sticker"
           (when (< 1 (count labels)) "s")
           ", which Instagram does not offer for this account."))))

(defn- instagram-destination
  [kind user result]
  (let [user-id (:instagram.user/id user)
        not-connected (:instagram.account/not-connected user)
        reel? (= :reels kind)
        sticker-warning (when-not reel?
                          (unsupported-stickers-text
                            (:instagram.user/unsupported-stickers user)))]
    (cond-> {:destination/key (destination-key kind user-id)
             :destination/kind kind
             :destination/label (if reel?
                                  "Instagram Reel"
                                  "Instagram Story")
             :instagram.user/id user-id
             :instagram.user/name (:instagram.user/name user)
             :instagram.user/profile-picture-url (:instagram.user/profile-picture-url user)}
      (not reel?)
      (assoc :share-to-fb? false)

      sticker-warning
      (assoc :destination/warning sticker-warning)

      not-connected
      (assoc :destination/disabled-reason "Disconnected")

      (and reel? (not not-connected) (not (:story-media/reel-ready? result)))
      (assoc :destination/disabled-reason "Instagram Reels require video media."))))

(defn- tiktok-destination
  [account result]
  (cond-> {:destination/key (destination-key :tiktok (:tiktok.user/id account))
           :destination/kind :tiktok
           :destination/label "TikTok"
           :tiktok.user/id (:tiktok.user/id account)
           :tiktok.user/name (:tiktok.user/name account)
           :tiktok.user/profile-picture (:tiktok.user/profile-picture account)
           :tiktok.user/profile-picture-url (:tiktok.user/profile-picture-url account)}
    (not (:story-media/blob-id result))
    (assoc :destination/disabled-reason "TikTok needs a prepared image or video.")))

(defn- youtube-destination
  [account result]
  (cond-> {:destination/key (destination-key :youtube (:youtube.user/id account))
           :destination/kind :youtube
           :destination/label "YouTube Short"
           :youtube.user/id (:youtube.user/id account)
           :youtube.user/name (:youtube.user/name account)
           :youtube.user/profile-picture (:youtube.user/profile-picture account)}
    (not (:story-media/video-blob-id result))
    (assoc :destination/disabled-reason "YouTube Shorts require a prepared video.")

    (and (:story-media/video-blob-id result)
         (duration-too-long? result))
    (assoc :destination/disabled-reason "YouTube Shorts can be at most 3 minutes long.")))

(defn- social-platform-name
  [kind]
  (:social-platform/name
    (social-platforms/by-id (:social-platform/id (social-platforms/post-type-by-kind kind)))))

(defn- social-media-missing-text
  [kind result]
  (let [post-type (social-platforms/post-type-by-kind kind)]
    (cond
      (:story-media/rendering? result)
      (str "This design renders as "
           (if (= :image (:story-media/expected-media result)) "an image" "a video")
           ", which " (:post-type/group-label post-type) " do not take.")

      (contains? (:post-type/media post-type) :image)
      (str (social-platform-name kind) " posts require a prepared image or video.")

      :else
      (str (social-platform-name kind) " posts require a prepared video."))))

(defn- rendering-warning
  [result]
  (str "Still rendering. The post goes out once the "
       (if (= :image (:story-media/expected-media result)) "image" "video")
       " is ready."))

(defn- social-destination
  "A destination of a bundle.social account for the post type of
   `kind`."
  [kind account result]
  (let [post-type (social-platforms/post-type-by-kind kind)]
    (cond-> {:destination/key (destination-key kind (:social.account/uuid account))
             :destination/kind kind
             :destination/label (:post-type/label post-type)
             :social.account/uuid (:social.account/uuid account)
             :social.account/name (:social.account/name account)
             :social.account/avatar-url (:social.account/avatar-url account)}
      (:social.account/disconnected-at account)
      (assoc :destination/disabled-reason
             (str "Disconnected. Reconnect the "
                  (social-platforms/account-noun (:social-platform/id post-type))
                  " under " social-platforms/accounts-page-label "."))

      (not (social-media kind result))
      (assoc :destination/disabled-reason (social-media-missing-text kind result))

      (social-too-long? kind result)
      (assoc :destination/disabled-reason (social-too-long-text kind))

      (social-too-short? kind result)
      (assoc :destination/disabled-reason (social-too-short-text kind))

      (= :pending (social-media kind result))
      (assoc :destination/warning (rendering-warning result)))))

(defn- platform-accounts
  "The connected accounts of the platform of the destination `kind`."
  [kind result]
  (let [platform-id (:social-platform/id (social-platforms/post-type-by-kind kind))]
    (filter #(= platform-id (:social.account/platform %))
            (:social/accounts result))))

(defn- own-youtube-accounts
  "The channels of Storrito's own YouTube integration, without the ones
   that are connected through bundle.social as well: the same channel
   would be offered twice and a Short posted twice (Tobias, 2026-10-06).
   Their posts scheduled before keep going out."
  [result]
  (let [bundle-channel-ids (into #{}
                                 (comp (filter #(= "YOUTUBE" (:social.account/platform %)))
                                       (map :social.account/platform-id))
                                 (:social/accounts result))]
    (remove #(contains? bundle-channel-ids (:youtube.user/id %))
            (:youtube/accounts result))))

(def ^:private own-platform-ids
  "The platform of each destination kind of Storrito's own YouTube and
   TikTok integrations."
  {:tiktok "TIKTOK"
   :youtube "YOUTUBE"})

(defn- mark-classic
  "Marks the destinations of the own YouTube and TikTok integrations
   when the same platform is offered through the new connection as
   well, so the two can be told apart (a TikTok account connected both
   ways cannot be matched)."
  [destinations]
  (let [new-platforms (into #{}
                            (keep #(:social-platform/id (social-platforms/post-type-by-kind
                                                          (:destination/kind %))))
                            destinations)]
    (mapv #(cond-> %
             (new-platforms (own-platform-ids (:destination/kind %)))
             (assoc :destination/classic? true))
          destinations)))

(defn- all-social-destinations
  [result]
  (let [instagram-users (vec (:instagram/users result))]
    (mark-classic
      (concat
        (map #(instagram-destination :instagram % result) instagram-users)
        (map #(instagram-destination :reels % result) instagram-users)
        (map #(tiktok-destination % result) (:tiktok/accounts result))
        (map #(youtube-destination % result) (own-youtube-accounts result))
        (for [kind (filter social-kind? (offered-platforms result))
              account (platform-accounts kind result)]
          (social-destination kind account result))))))

(defn- selected-destination-entry
  [destination]
  (select-keys destination
               [:destination/key
                :destination/kind
                :destination/label
                :destination/classic?
                :instagram.user/id
                :instagram.user/name
                :instagram.user/profile-picture-url
                :share-to-fb?
                :tiktok.user/id
                :tiktok.user/name
                :tiktok.user/profile-picture
                :tiktok.user/profile-picture-url
                :youtube.user/id
                :youtube.user/name
                :youtube.user/profile-picture
                :social.account/uuid
                :social.account/name
                :social.account/avatar-url]))

(defn reconcile-selected-destinations
  [state result]
  (let [available-by-key (into {}
                               (map (juxt :destination/key identity))
                               (remove :destination/disabled-reason
                                       (all-social-destinations result)))]
    (into []
          (keep (fn [saved]
                  (when-let [available (available-by-key (:destination/key saved))]
                    (merge (selected-destination-entry available)
                           (select-keys saved
                                        [:share-to-fb?
                                         :facebook-linkage/destination
                                         :facebook-linkage/type
                                         :highlight-id])))))
          (selected-destinations state))))

(defn default-instagram-destination
  "The Story destination of the only connected Instagram account, so a
   visit without a remembered selection (typically the first one during
   onboarding) does not start with nothing selected. Nil when zero or
   several accounts are connected or the account cannot be chosen."
  [result]
  (let [users (connected-instagram-users result)]
    (when (= 1 (count users))
      (let [destination (instagram-destination :instagram (first users) result)]
        (when-not (:destination/disabled-reason destination)
          (selected-destination-entry destination))))))

(defn with-default-destination
  "Falls back to the only connected Instagram account when nothing is
   selected."
  [selected result]
  (if (empty? selected)
    (if-let [destination (default-instagram-destination result)]
      [destination]
      [])
    (vec selected)))

(defn toggle-destination
  [selected destination]
  (let [key (:destination/key destination)]
    (if (some #(= (:destination/key %) key) selected)
      (vec (remove #(= (:destination/key %) key) selected))
      (conj (vec selected)
            (selected-destination-entry destination)))))

(def ^:private default-youtube-privacy
  "The visibility a YouTube Short starts with (Tobias, 2026-10-06)."
  "public")

(def ^:private youtube-privacy-choices
  [["private" "Private"]
   ["unlisted" "Unlisted"]
   ["public" "Public"]])

(def ^:private remembered-store-checks
  "The options that are a habit rather than a decision per post, each
   with the check of a valid value; they start from the last chosen
   value (see `remembered-options`). Not remembered: titles, texts,
   links, covers and AI labels, which belong to one post, and the
   TikTok card, whose privacy level TikTok's posting guidelines require
   to be chosen for every post."
  {::youtube-privacy-status (set (map first youtube-privacy-choices))
   ::youtube-made-for-kids boolean?
   ::reels-recommend-facebook boolean?
   ::reels-recommend-feed boolean?})

(defn- choice-options
  "The options of the post type of `kind` that are a choice (a select),
   e.g. LinkedIn's visibility. None of a post type that shares an
   options card of Storrito's own integration: the card decides what is
   remembered, and the TikTok card (also bundle.social's TikTok
   privacy) must never be."
  [kind]
  (let [post-type (social-platforms/post-type-by-kind kind)]
    (when-not (:post-type/options-card post-type)
      (filter #(and (nil? (:option/type %))
                    (seq (:option/choices %)))
              (:post-type/options post-type)))))

(defn- remembered-social-options
  "The values of `social-options` (`::social-options`) that are a valid
   choice of a choice option."
  [social-options]
  (into {}
        (keep (fn [[kind values]]
                (let [kept (into {}
                                 (keep (fn [{:option/keys [key choices]}]
                                         (let [value (get values key)]
                                           (when (some #(= value (:choice/value %)) choices)
                                             [key value]))))
                                 (choice-options kind))]
                  (when (seq kept)
                    [kind kept]))))
        social-options))

(defn remembered-options
  "The options of `m` (the store or what the browser remembered) that
   start the next post: the valid values of `remembered-store-checks` and
   the choice options of the bundle.social post types, so a user does
   not choose the same visibility for every post."
  [m]
  (into {::social-options (remembered-social-options (::social-options m))}
        (keep (fn [[k valid?]]
                (let [value (get m k)]
                  (when (valid? value)
                    [k value]))))
        remembered-store-checks))

(defn- options-card
  "The options card of Storrito's own integration that the post type of
   `kind` shares (`:post-type/options-card`), nil for a generic one."
  [kind]
  (:post-type/options-card (social-platforms/post-type-by-kind kind)))

(defn- options-card-values
  "The fields of an options card of Storrito's own integration, as the
   options of a bundle.social post type that shares the card."
  [state card]
  (case card
    :youtube (cond-> {:privacy (or (::youtube-privacy-status state) default-youtube-privacy)
                      :made-for-kids (boolean (::youtube-made-for-kids state))
                      :ai-generated (boolean (::youtube-synthetic-media state))}
               (::youtube-title state)
               (assoc :title (::youtube-title state)))
    :tiktok (cond-> {:disable-comments (boolean (::tiktok-disable-comment state))
                     :disable-duet (boolean (::tiktok-disable-duet state))
                     :disable-stitch (boolean (::tiktok-disable-stitch state))
                     :ai-generated (boolean (::tiktok-ai-generated state))
                     :brand-content (boolean (::tiktok-branded-content state))
                     :brand-organic (boolean (::tiktok-brand-organic state))
                     :auto-add-music (boolean (::tiktok-auto-audio state))}
              (::tiktok-privacy-level state)
              (assoc :privacy (::tiktok-privacy-level state))

              (tiktok-video-cover-timestamp-ms state)
              (assoc :cover-ms (tiktok-video-cover-timestamp-ms state)))
    {}))

(defn- social-options
  "The option values chosen for the destinations of `kind`, see
   `:post-type/options`; the schedule command fills in the defaults. A
   post type that shares an options card of Storrito's own integration
   takes the card's fields."
  [state kind]
  (if-let [card (options-card kind)]
    (options-card-values state card)
    (get-in state [::social-options kind] {})))

(defn- options-card-selected?
  "A destination is selected whose post type shares the options `card`."
  [state card]
  (some #(= card (options-card %)) (selected-platforms state)))

(defn- tiktok-description
  "The text of a TikTok photo: the card's photo description, or the
   caption."
  [state]
  (str/trim (or (::tiktok-description state) (caption state))))

(defn- social-text
  "The text of a bundle.social post: the caption, or for a TikTok photo
   the card's photo description, like Storrito's own TikTok integration."
  [state result kind]
  (if (and (= :tiktok (options-card kind))
           (:story-media/image-post? result))
    (tiktok-description state)
    (caption state)))

(defn- social-platform-error
  [kind state result]
  (let [post-type (social-platforms/post-type-by-kind kind)
        noun (social-platforms/account-noun (:social-platform/id post-type))
        caption-error (social-platforms/caption-error (:social-platform/id post-type)
                                                      (:post-type/id post-type)
                                                      (social-text state result kind))
        options-error (social-platforms/options-error (:social-platform/id post-type)
                                                      (:post-type/id post-type)
                                                      (social-options state kind))]
    (cond
      (empty? (platform-accounts kind result))
      (str "Connect a " noun " first.")

      (not (social-media kind result))
      (social-media-missing-text kind result)

      (social-too-long? kind result)
      (social-too-long-text kind)

      (social-too-short? kind result)
      (social-too-short-text kind)

      (empty? (selected-social-account-uuids state kind))
      (str "Select at least one " noun ".")

      caption-error
      caption-error

      (and (= :youtube (options-card kind))
           (str/blank? (youtube-title state result)))
      "Add a YouTube title."

      options-error
      options-error

      (not (social-platforms/credits-allow? (:social-platform/id post-type) (:post-type/id post-type) result))
      (if (and (:organization/legacy-customer? result)
               (:post-type/always-billed? post-type))
        (str "Posting to " (:social-platform/name (social-platforms/by-id (:social-platform/id post-type)))
             " is paid with credits, which your plan does not include. Switch to a current plan on the Subscription page.")
        "Your credit balance is empty.")

      :else nil)))

(defn- platform-error
  [platform state result]
  (case platform
    :instagram
    (cond
      (empty? (connected-instagram-users result))
      "Connect an Instagram account first."

      (empty? (selected-instagram-accounts state))
      "Select at least one Instagram account."

      (not (instagram-allowed? result))
      "Your credit balance is empty."

      :else nil)

    :reels
    (cond
      (empty? (connected-instagram-users result))
      "Connect an Instagram account first."

      (not (:story-media/reel-ready? result))
      "Instagram Reels require video media."

      (empty? (selected-reels-ids state))
      "Select at least one Instagram account."

      (not (paid-platform-allowed? result))
      "Your credit balance is empty."

      :else nil)

    :tiktok
    (cond
      (empty? (:tiktok/accounts result))
      "Connect a TikTok account first."

      (not (:story-media/blob-id result))
      "TikTok scheduling needs a prepared image or video."

      (empty? (selected-tiktok-ids state))
      "Select at least one TikTok account."

      (some #(:tiktok/creator-info-error (tiktok-creator-info-result state %))
            (selected-tiktok-ids state))
      "Reconnect the TikTok account, or deselect it to schedule the others."

      (not-every? #(tiktok-privacy-level-selected? state %)
                  (selected-tiktok-ids state))
      "Choose who can view this TikTok post."

      ;; TikTok's content sharing guidelines.
      (and (::tiktok-branded-content state)
           (= "SELF_ONLY" (::tiktok-privacy-level state)))
      "A TikTok partnership cannot be posted as \"Only me\". Choose another audience."

      (not (paid-platform-allowed? result))
      "Your credit balance is empty."

      :else nil)

    :youtube
    (cond
      (empty? (:youtube/accounts result))
      "Connect a YouTube channel first."

      (not (:story-media/video-blob-id result))
      "YouTube Shorts require a prepared video."

      (duration-too-long? result)
      "YouTube Shorts can be at most 3 minutes long."

      (empty? (selected-youtube-ids state))
      "Select at least one YouTube channel."

      (not (seq (youtube-title state result)))
      "Add a YouTube title."

      (not (paid-platform-allowed? result))
      "Your credit balance is empty."

      :else nil)

    (social-platform-error platform state result)))

(defn- social-command
  [state result kind account-uuid]
  {:command/kind :command/social-post-schedule
   :command/data (cond-> {:social.account/uuid account-uuid
                          :story-media/eid (:story-media/eid result)
                          :post/type (:post-type/id (social-platforms/post-type-by-kind kind))
                          :text (social-text state result kind)
                          :options (social-options state kind)}
                   (selected-date-ms state)
                   (assoc :social.post/date (selected-date-ms state)))})

(defn- instagram-command
  [state result]
  {:command/kind :command/story-publish
   :command/data (cond-> {:story-media/eid (:story-media/eid result)
                          :multi-post (mapv (fn [entry]
                                              (cond-> {:instagram.user/id (:instagram.user/id entry)
                                                       :share-to-fb? (boolean (:share-to-fb? entry))}
                                                (:share-to-fb? entry)
                                                (merge (select-keys entry
                                                                    [:facebook-linkage/destination
                                                                     :facebook-linkage/type]))
                                                (not-empty (:highlight-id entry))
                                                (assoc :highlight-id (:highlight-id entry))))
                                            (selected-instagram-accounts state))}
                   (selected-date-ms state)
                   (assoc :story-post/date-ms (selected-date-ms state))
                   (seq (story-first-comment state))
                   (assoc :story-post/first-comment (story-first-comment state))
                   (::story-ai-generated state)
                   (assoc :story-post/ai-generated? true))})

(defn- tiktok-command
  [state result account-id]
  (let [image-post? (:story-media/image-post? result)
        privacy-levels (:privacy_level_options (tiktok-creator-info state account-id))]
    {:command/kind :command/tiktok-schedule
     :command/data (cond-> {:tiktok.user/id account-id
                            :story-media/eid (:story-media/eid result)
                            :blob-id (:story-media/blob-id result)
                            :image-post? image-post?
                            :title (if image-post?
                                     (str/trim (or (::tiktok-title state) ""))
                                     (caption state))
                            :description (if image-post?
                                           (tiktok-description state)
                                           (caption state))
                            :privacy-level (selected-tiktok-privacy-level state privacy-levels)
                            :disable-comment? (boolean (::tiktok-disable-comment state))
                            :disable-duet? (boolean (::tiktok-disable-duet state))
                            :disable-stitch? (boolean (::tiktok-disable-stitch state))
                            :auto-audio? (boolean (::tiktok-auto-audio state))
                            :ai-generated? (boolean (::tiktok-ai-generated state))
                            :branded-content? (boolean (::tiktok-branded-content state))
                            :brand-organic? (boolean (::tiktok-brand-organic state))}
                     (selected-date-ms state)
                     (assoc :tiktok.post/date (selected-date-ms state))
                     (not image-post?)
                     (assoc :video-cover-timestamp-ms
                            (tiktok-video-cover-timestamp-ms state)))}))

(defn- reels-command
  [state result account-id]
  {:command/kind :command/reel-schedule
   :command/data (cond-> {:instagram.user/id account-id
                          :story-media/eid (:story-media/eid result)
                          :blob/uuid (:reels-publish/video-blob-id result)
                          :blob/name (:reels-publish/video-blob-name result)
                          :blob/preview (:reels-publish/preview-blob-id result)
                          :instagram/caption (caption state)
                          :instagram/recommend-facebook (not= false (::reels-recommend-facebook state))
                          :instagram/recommend-feed (boolean (::reels-recommend-feed state))
                          :instagram/ai-generated? (boolean (::reels-ai-generated state))}
                   (selected-date-ms state)
                   (assoc :reel/post-date (selected-date-ms state))
                   (::reels-cover-blob-id state)
                   (assoc :blob-cover/uuid (::reels-cover-blob-id state)
                          :blob-cover/name (::reels-cover-name state)))})

(defn- youtube-command
  [state result account-id]
  {:command/kind :command/youtube-short-schedule
   :command/data (cond-> {:youtube.user/id account-id
                          :story-media/eid (:story-media/eid result)
                          :blob-id (:story-media/video-blob-id result)
                          :title (youtube-title state result)
                          :description (caption state)
                          :privacy-status (or (::youtube-privacy-status state) default-youtube-privacy)
                          :made-for-kids? (boolean (::youtube-made-for-kids state))
                          :synthetic-media? (boolean (::youtube-synthetic-media state))}
                   (selected-date-ms state)
                   (assoc :youtube.short/date (selected-date-ms state)))})

(defn- platform-commands
  [platform state result]
  (case platform
    :instagram [(instagram-command state result)]
    :reels (mapv #(reels-command state result %)
                 (selected-reels-ids state))
    :tiktok (mapv #(tiktok-command state result %)
                  (selected-tiktok-ids state))
    :youtube (mapv #(youtube-command state result %)
                   (selected-youtube-ids state))
    (mapv #(social-command state result platform %)
          (selected-social-account-uuids state platform))))

(defn- selected-errors
  [state result]
  (keep (fn [platform]
          (when (platform-selected? state platform)
            (when-let [error (platform-error platform state result)]
              {:platform platform
               :error error})))
        (offered-platforms result)))

(defn- selected-rendering-destinations
  "The selected bundle.social destinations that wait for the story
   media's rendering; the page says so above the submit button, since
   the hint in the account list is hidden once a destination is
   selected."
  [state result]
  (filter #(= :pending (social-media (:destination/kind %) result))
          (selected-destinations state)))

(defn- selected-sticker-warnings
  "Warnings for chosen Instagram Story accounts whose sticker tray does
   not offer a sticker (e.g. Poll or Quiz) that the design contains.
   Non-blocking: the user can still schedule the story."
  [state result]
  (let [users-by-id (into {}
                          (map (juxt :instagram.user/id identity))
                          (:instagram/users result))]
    (vec
      (keep (fn [entry]
              (let [user (get users-by-id (:instagram.user/id entry))
                    warning (unsupported-stickers-text
                              (:instagram.user/unsupported-stickers user))]
                (when warning
                  {:instagram.user/id (:instagram.user/id user)
                   :instagram.user/name (:instagram.user/name user)
                   :warning warning})))
            (selected-instagram-accounts state)))))

(defn- commands
  [state result]
  (->> (offered-platforms result)
       (filter #(platform-selected? state %))
       (remove #(platform-error % state result))
       (mapcat #(platform-commands % state result))
       vec))

(defn- schedule-date-changed?
  [state]
  (boolean (seq (::schedule-date-str state))))

(defn- submit-button-label
  [state selected issued? duplicate?]
  (let [plural? (< 1 (count selected))]
    (cond
      issued? (if (schedule-date-changed? state)
                "Scheduling..."
                "Posting...")
      duplicate? "Already scheduled for this minute"
      (empty? selected) "Select a destination"
      (schedule-date-changed? state) (if plural?
                                      "Schedule to selected destinations"
                                      "Schedule to selected destination")
      plural? "Post now to selected destinations"
      :else "Post now")))

(defn- platform-title
  [platform]
  (case platform
    :instagram "Instagram Stories"
    :reels "Instagram Reels"
    :tiktok "TikTok"
    :youtube "YouTube Shorts"
    (:post-type/group-label (social-platforms/post-type-by-kind platform))))

(defn- cover-preview-section
  [state]
  (let [cover-blob-id (::reels-cover-blob-id state)
        cover-name (::reels-cover-name state)
        uploading? (::reels-cover-uploading? state)]
    (when (or cover-blob-id uploading?)
      [:div
       {:class ["mt-3" "rounded-lg" "border" "border-gray-200" "bg-white" "p-2"]}
       [:div {:class ["text-xs" "font-medium" "text-gray-700"]}
        "Reel cover photo"]
       (if cover-blob-id
         [:img {:src (str "/image/" cover-blob-id)
                :alt (or cover-name "Reel cover photo")
                :class ["mt-1.5" "w-full" "rounded-md" "bg-gray-100" "object-cover"]
                :style {:aspect-ratio "9/16"}}]
         [:div
          {:class ["mt-1.5" "flex" "items-center" "justify-center" "rounded-md"
                   "bg-gray-100" "text-xs" "text-gray-500"]
           :style {:aspect-ratio "9/16"}}
          "Uploading cover..."])
       (when cover-name
         [:div {:class ["mt-1.5" "truncate" "text-xs" "text-gray-500"]}
          cover-name])])))

(defn- preview-section
  [state result]
  (let [preview-url (:story-media/preview-url result)
        video-url (:story-media/video-url result)]
    [:div
     {:class ["w-full" "sm:w-48" "md:w-56" "shrink-0"]}
     [:div
      {:class ["relative" "rounded-lg" "overflow-hidden" "bg-gray-900" "shadow-lg"]
       :style {:aspect-ratio "9/16"}}
      (cond
        video-url
        [:video {:src video-url
                 :poster preview-url
                 :controls true
                 :class ["w-full" "h-full" "object-cover"]}]

        preview-url
        [:img {:src preview-url
               :class ["w-full" "h-full" "object-cover"]}]

        :else
        [:div
         {:class ["w-full" "h-full" "flex" "items-center" "justify-center"
                  "text-gray-500" "text-sm"]}
         "No preview"])]
     (cover-preview-section state)]))

(defn- account-avatar
  ([url name icon]
   (account-avatar url name icon ["h-9" "w-9"]))
  ([url name icon size-classes]
   [:div
    {:class (into size-classes
                  ["rounded-full" "overflow-hidden" "bg-gray-100" "shrink-0"
                   "flex" "items-center" "justify-center"])}
    (if url
      [:img {:src url
             :class ["h-full" "w-full" "object-cover"]}]
      [:span {:class ["text-gray-400"]} icon])]))

(defn- destination-name
  [destination]
  (or (:instagram.user/name destination)
      (:tiktok.user/name destination)
      (:youtube.user/name destination)
      (:social.account/name destination)))

(defn- destination-avatar-url
  [destination]
  (or (:instagram.user/profile-picture-url destination)
      (:tiktok.user/profile-picture-url destination)
      (:tiktok.user/profile-picture destination)
      (:youtube.user/profile-picture destination)
      (:social.account/avatar-url destination)))

(defn- kind-social-platform
  "The bundle.social platform of the destination `kind`, nil for
   Storrito's own platforms."
  [kind]
  (some-> (social-platforms/post-type-by-kind kind)
          :social-platform/id
          (social-platforms/by-id)))

(defn- destination-icon
  [destination classes]
  (if-let [platform (kind-social-platform (:destination/kind destination))]
    ((:social-platform/icon platform) classes)
    (case (:destination/kind destination)
      :tiktok (icons/tiktok-logo classes)
      :youtube (icons/youtube-logo classes)
      (icons/instagram-logo classes))))

(defn- destination-icon-color
  [destination]
  (if-let [platform (kind-social-platform (:destination/kind destination))]
    (:social-platform/text-class platform)
    (case (:destination/kind destination)
      :tiktok ["text-gray-900"]
      :youtube ["text-red-600"]
      :reels ["text-fuchsia-600"]
      ["text-pink-500"])))

(defn- destination-pill-class
  [destination]
  (if-let [platform (kind-social-platform (:destination/kind destination))]
    (:social-platform/pill-class platform)
    (case (:destination/kind destination)
      :tiktok ["bg-gray-50" "border-gray-200" "text-gray-800"]
      :youtube ["bg-red-50" "border-red-200" "text-red-700"]
      :reels ["bg-fuchsia-50" "border-fuchsia-200" "text-fuchsia-700"]
      ["bg-pink-50" "border-pink-200" "text-pink-700"])))

(defn- classic-mark
  "\"Classic\" on a destination of the own YouTube or TikTok integration
   next to the same platform's new connection (`mark-classic`)."
  [destination]
  (when (:destination/classic? destination)
    [:span
     {:class ["shrink-0" "rounded" "bg-gray-100" "px-1" "py-0.5" "text-[10px]"
              "font-medium" "uppercase" "tracking-wide" "text-gray-600"]
      :title "Connected with Storrito's original connection"}
     "Classic"]))

(defn- destination-platform-mark
  [destination]
  [:span
   {:class ["inline-flex" "items-center" "gap-1" "shrink-0" "rounded-full"
            "bg-white/70" "px-1.5" "py-0.5" "text-[10px]" "font-medium"]
    :title (:destination/label destination)}
   [:span {:class (destination-icon-color destination)}
    (destination-icon destination ["h-3.5" "w-3.5"])]
   (if-let [mark (:post-type/mark (social-platforms/post-type-by-kind
                                      (:destination/kind destination)))]
     [:span mark]
     (case (:destination/kind destination)
       :reels [:span "Reels"]
       nil))])

(defn selected-destination?
  [selected destination]
  (some #(= (:destination/key %) (:destination/key destination)) selected))

(defn- destination-group
  "The destination group of `kind` in the account list: a post type of
   bundle.social can join the group of Storrito's own integration
   (`:post-type/destination-group`), like YouTube channels connected
   through bundle.social next to the ones connected before."
  [kind]
  (or (:post-type/destination-group (social-platforms/post-type-by-kind kind))
      kind))

(def ^:private destination-group-order
  (into [] (comp (map destination-group) (distinct)) platforms))

(def ^:private destination-group-labels
  (into {:instagram "Instagram Stories"
         :reels "Reels"
         :tiktok "TikTok"
         :youtube "YouTube"}
        (map (juxt :post-type/kind :post-type/group-label))
        (social-platforms/post-types)))

(def ^:private account-listbox-dom-id "social-account-combobox-listbox")

(def ^:private account-trigger-dom-id "social-account-combobox-trigger")

(defn- destination-option-dom-id
  [destination-key]
  (str "social-destination-option-" destination-key))

(defn- destination-group-dom-id
  [kind]
  (str "social-account-combobox-group-" (name kind)))

(defn- focus-account-trigger!
  "Moves focus back to the trigger button. Per the APG, closing the popup
   with the keyboard returns focus to the combobox. Dispatching renders
   synchronously, so the trigger usually exists right away; the
   requestAnimationFrame covers a throttled (deferred) render."
  []
  (if-let [node (js/document.getElementById account-trigger-dom-id)]
    (.focus node)
    (js/requestAnimationFrame
      (fn []
        (some-> (js/document.getElementById account-trigger-dom-id)
                (.focus))))))

(defn- scroll-destination-into-view!
  [destination-key]
  (when destination-key
    (js/requestAnimationFrame
      (fn []
        (some-> (js/document.getElementById
                  (destination-option-dom-id destination-key))
                (.scrollIntoView #js {:block "nearest"}))))))

(defn- close-account-dropdown-actions []
  [[:store/assoc ::social-account-dropdown-open false]
   [:store/dissoc ::social-account-search]
   [:store/dissoc ::social-account-active-key]])

(defn- filtered-destination-groups
  "Groups the destinations by platform in a fixed order, keeping only
   destinations whose account name or label matches `query`. Groups
   without members are omitted."
  [destinations query]
  (let [matches (if (seq query)
                  (filter (fn [destination]
                            (or (str/includes?
                                  (str/lower-case
                                    (or (destination-name destination) ""))
                                  query)
                                (str/includes?
                                  (str/lower-case
                                    (or (:destination/label destination) ""))
                                  query)))
                          destinations)
                  destinations)
        by-group (group-by (comp destination-group :destination/kind) matches)]
    (keep (fn [kind]
            (when-let [members (seq (get by-group kind))]
              {:group/kind kind
               :group/label (get destination-group-labels kind)
               :group/destinations members}))
          destination-group-order)))

(defn moved-destination-key
  "Returns the :destination/key `delta` steps away from `active-key`,
   wrapping around. With no current active option, ArrowDown starts at
   the first and ArrowUp at the last destination."
  [destinations active-key delta]
  (let [keys* (mapv :destination/key destinations)
        n (count keys*)
        idx (first (keep-indexed (fn [i k]
                                   (when (= k active-key) i))
                                 keys*))]
    (when (pos? n)
      (nth keys* (if idx
                   (mod (+ idx delta) n)
                   (if (pos? delta) 0 (dec n)))))))

(defn- account-input-keydown-fn
  "Keyboard interaction of the open multi-select combobox per the APG:
   ArrowDown/ArrowUp move the highlight over the enabled destinations,
   Enter toggles the highlighted destination without closing the popup,
   Escape and Alt+ArrowUp close and return focus, Tab closes."
  [{:keys [state enabled-destinations active-key query]}]
  (fn [e]
    (let [dispatch! (:replicant/dispatch! state)
          dispatch-actions! (fn [actions]
                              (dispatch! {:actions actions}))
          key (.-key e)]
      (case key
        "ArrowDown"
        (do (.preventDefault e)
            (when-let [k (moved-destination-key enabled-destinations
                                                active-key 1)]
              (dispatch-actions! [[:store/assoc ::social-account-active-key k]])
              (scroll-destination-into-view! k)))

        "ArrowUp"
        (do (.preventDefault e)
            (if (.-altKey e)
              (do (dispatch-actions! (close-account-dropdown-actions))
                  (focus-account-trigger!))
              (when-let [k (moved-destination-key enabled-destinations
                                                  active-key -1)]
                (dispatch-actions! [[:store/assoc ::social-account-active-key k]])
                (scroll-destination-into-view! k))))

        "Enter"
        (do (.preventDefault e)
            (when-let [destination
                       (or (some #(when (= (:destination/key %) active-key) %)
                                 enabled-destinations)
                           (when (seq query)
                             (first enabled-destinations)))]
              (dispatch-actions! [[:social-scheduling/toggle-destination
                                   destination]])))

        "Escape"
        (do (.preventDefault e)
            (dispatch-actions! (close-account-dropdown-actions))
            (focus-account-trigger!))

        ;; No preventDefault, but the focus-account-trigger! is still
        ;; required: the dispatch renders synchronously and removes this
        ;; input, so without it the browser's default Tab would continue
        ;; from <body> instead of from the combo box.
        "Tab"
        (do (dispatch-actions! (close-account-dropdown-actions))
            (focus-account-trigger!))

        nil))))

(defn- account-trigger-keydown-fn
  "ArrowDown/ArrowUp on the closed combobox open the popup."
  [state]
  (fn [e]
    (let [key (.-key e)]
      (when (or (= key "ArrowDown")
                (= key "ArrowUp"))
        (.preventDefault e)
        ((:replicant/dispatch! state)
         {:actions [[:store/assoc ::social-account-dropdown-open true]]})))))

;; z-index tiers: the popup (z-40) and its backdrop (z-30) sit above
;; page content but below fixed chrome like toasts (z-50) - the same
;; tiers as ui.account-selector.

(defn- destination-option-row
  [{:keys [destination selected active-key]}]
  (let [destination-key (:destination/key destination)
        selected? (selected-destination? selected destination)
        active? (= destination-key active-key)
        disabled-reason (:destination/disabled-reason destination)]
    [:button
     {:type "button"
      :role "option"
      :id (destination-option-dom-id destination-key)
      :aria-selected (str (boolean selected?))
      :tabindex "-1"
      :disabled (boolean disabled-reason)
      :replicant/key destination-key
      :class (into ["flex" "w-full" "appearance-none" "items-center"
                    "gap-2.5" "px-3" "py-2" "text-left" "text-sm"
                    "transition-colors"]
                   (cond
                     disabled-reason
                     ["opacity-50" "cursor-default" "text-gray-700"]

                     (and selected? active?)
                     ["bg-indigo-100" "text-indigo-700" "cursor-pointer"]

                     selected?
                     ["bg-indigo-50" "text-indigo-700" "cursor-pointer"]

                     active?
                     ["bg-gray-100" "text-gray-700" "cursor-pointer"]

                     :else
                     ["text-gray-700" "hover:bg-gray-50" "cursor-pointer"]))
      :on {:click (when-not disabled-reason
                    [[:social-scheduling/toggle-destination destination]])}}
     (account-avatar (destination-avatar-url destination)
                     (destination-name destination)
                     [:span {:class (destination-icon-color destination)}
                      (destination-icon destination ["h-4" "w-4"])]
                     ["h-6" "w-6"])
     [:div
      {:class ["flex-1" "min-w-0"]}
      [:div {:class ["flex" "items-center" "gap-1.5" "min-w-0"]}
       [:span {:class ["truncate"]
               :title (destination-name destination)}
        (destination-name destination)]
       (classic-mark destination)]
      (when disabled-reason
        [:div {:class ["text-xs" "text-amber-600"]}
         disabled-reason])
      (when-not disabled-reason
        (when-let [warning (:destination/warning destination)]
          [:div {:class ["text-xs" "text-amber-600"]}
           warning]))]
     [:div
      {:class (into ["w-4" "h-4" "rounded" "border-2" "flex"
                     "items-center" "justify-center" "shrink-0"]
                    (if selected?
                      ["border-indigo-500" "bg-indigo-500"]
                      ["border-gray-300"]))}
      (when selected?
        (icons/checkmark ["w-3" "h-3" "text-white"]))]]))

(defn- destination-group-section
  [{:group/keys [kind label destinations]} selected active-key]
  [:div
   {:replicant/key kind
    :role "group"
    :aria-labelledby (destination-group-dom-id kind)
    :class ["pb-1"]}
   [:div
    {:id (destination-group-dom-id kind)
     :class ["flex" "items-center" "gap-1.5" "px-3" "pt-2" "pb-1"
             "text-xs" "font-semibold" "uppercase" "tracking-wide"
             "text-gray-400"]}
    [:span {:class (destination-icon-color {:destination/kind kind})}
     (destination-icon {:destination/kind kind} ["w-3.5" "h-3.5"])]
    label]
   (map (fn [destination]
          (destination-option-row {:destination destination
                                   :selected selected
                                   :active-key active-key}))
        destinations)])

(defn- social-account-selector
  [state result]
  (let [destinations (all-social-destinations result)
        selected (selected-destinations state)
        dropdown-open (::social-account-dropdown-open state)
        input-value (or (::social-account-search state) "")
        query (str/trim (str/lower-case input-value))
        groups (filtered-destination-groups destinations query)
        enabled-destinations (vec (remove :destination/disabled-reason
                                          (mapcat :group/destinations groups)))
        active-key (some #(when (= (:destination/key %)
                                   (::social-account-active-key state))
                            (:destination/key %))
                         enabled-destinations)]
    [:div
     {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-3"]}
     [:div
      {:class ["flex" "items-start" "justify-between" "gap-3"]}
      [:div
       [:h2
        {:class ["text-sm" "font-semibold" "text-gray-900"]}
        "Social accounts"]
       [:p
        {:class ["mt-1" "text-xs" "text-gray-500"]}
        "Select one or more accounts to post to. Accounts are grouped by platform."]]
      [:span
       {:class ["shrink-0" "rounded-full" "bg-gray-100" "px-2.5" "py-1"
                "text-xs" "font-medium" "text-gray-600"]}
       (if (seq selected)
         (str (count selected) " selected")
         "None selected")]]
     (if (seq destinations)
       [:div
        {:class ["space-y-2"]}
        [:div
         {:class ["relative"]}
         (if dropdown-open
           [:div
            {:class ["relative" "z-40" "flex" "items-center" "h-10"
                     "rounded-lg" "border" "border-indigo-300" "bg-white"
                     "ring-2" "ring-indigo-100"]}
            [:div
             {:class ["absolute" "left-0" "top-0" "bottom-0" "w-9" "flex"
                      "items-center" "justify-center" "text-gray-400"
                      "pointer-events-none"]}
             (icons/search ["w-4" "h-4"])]
            [:input
             (cond-> {:type "text"
                      :role "combobox"
                      :aria-expanded "true"
                      :aria-controls account-listbox-dom-id
                      :aria-autocomplete "list"
                      :aria-label "Search social accounts"
                      :autocomplete "off"
                      :placeholder "Search accounts…"
                      :value input-value
                      :replicant/on-mount (fn [{:keys [replicant/node]}]
                                            (.focus node))
                      :class ["w-full" "h-full" "pl-9" "pr-3" "rounded-lg"
                              "text-sm" "bg-transparent" "outline-none"
                              "border-0"]
                      :on {:input [[:store/assoc ::social-account-search
                                    :event/target.value]
                                   [:store/dissoc ::social-account-active-key]]
                           :keydown (account-input-keydown-fn
                                      {:state state
                                       :enabled-destinations enabled-destinations
                                       :active-key active-key
                                       :query query})}}
               active-key
               (assoc :aria-activedescendant
                      (destination-option-dom-id active-key)))]]
           [:button
            {:type "button"
             :id account-trigger-dom-id
             :aria-haspopup "listbox"
             :aria-expanded "false"
             :class ["w-full" "flex" "items-center" "justify-between" "gap-2"
                     "px-3" "py-2.5" "text-sm" "border" "border-gray-300"
                     "rounded-lg" "bg-white" "hover:bg-gray-50" "shadow-sm"
                     "cursor-pointer" "transition-colors"]
             :on {:click [[:store/assoc ::social-account-dropdown-open true]]
                  :keydown (account-trigger-keydown-fn state)}}
            [:div
             {:class ["flex" "items-center" "gap-2" "flex-1" "min-w-0"]}
             (if (seq selected)
               [:div
                {:class ["flex" "items-center" "gap-1.5" "flex-wrap"]}
                (map
                  (fn [destination]
                    [:div
                     {:class (into ["flex" "items-center" "gap-1" "border" "rounded-full"
                                    "pl-0.5" "pr-1" "py-0.5"]
                                   (destination-pill-class destination))
                      :replicant/key (:destination/key destination)}
                     (account-avatar (destination-avatar-url destination)
                                     (destination-name destination)
                                     [:span {:class (destination-icon-color destination)}
                                      (destination-icon destination ["h-3" "w-3"])]
                                     ["h-5" "w-5"])
                     [:span
                      {:class ["text-xs" "truncate" "max-w-[120px]"]
                       :title (destination-name destination)}
                      (destination-name destination)]
                     (classic-mark destination)
                     (destination-platform-mark destination)
                     [:span
                      {:class ["ml-0.5" "w-4" "h-4" "flex" "items-center"
                               "justify-center" "rounded-full" "opacity-70"
                               "hover:bg-white/70" "hover:opacity-100"
                               "cursor-pointer" "leading-none" "text-sm"]
                       :title (str "Remove " (:destination/label destination) " "
                                   (destination-name destination))
                       :on {:click [[:js/stop-propagation]
                                    [:social-scheduling/toggle-destination
                                     destination]]}}
                      "×"]])
                  selected)]
               [:span {:class ["text-gray-400"]}
                "Select social accounts..."])]
            (icons/chevron-down
              (into ["w-4" "h-4" "text-gray-400" "shrink-0"
                     "transition-transform"]
                    (when dropdown-open ["rotate-180"])))])
         (when dropdown-open
           [:div
            {:class ["fixed" "inset-0" "z-30"]
             :aria-hidden "true"
             :on {:click (close-account-dropdown-actions)}}])
         (when dropdown-open
           [:div
            {:class ["absolute" "z-40" "mt-1" "w-full" "bg-white" "border"
                     "border-gray-200" "rounded-lg" "shadow-lg"
                     "max-h-80" "overflow-y-auto"]}
            (if (empty? groups)
              [:div {:class ["px-3" "py-3" "text-sm" "text-gray-400"]}
               "No matches"]
              [:div
               {:id account-listbox-dom-id
                :role "listbox"
                :aria-label "Social accounts"
                :aria-multiselectable "true"
                :class ["divide-y" "divide-gray-200"]}
               (map #(destination-group-section % selected active-key)
                    groups)])
            [:div
             {:class ["border-t" "border-gray-200"]}
             [:button
              {:type "button"
               :class ["flex" "w-full" "items-center" "gap-2.5" "px-3" "py-2"
                       "text-left" "text-sm" "text-gray-500" "hover:bg-gray-50"
                       "cursor-pointer"]
               :on {:click (into (close-account-dropdown-actions)
                                 [[:ui/modal-open :modals/social-account-connect {:source "schedule-page"}]])}}
              [:div {:class ["w-6" "h-6" "rounded-full" "border" "border-dashed"
                             "border-gray-300" "flex" "items-center"
                             "justify-center" "text-gray-400" "text-sm"
                             "shrink-0"]}
               "+"]
              "Connect account"]]])]]
       ;; Without a connected account the page is a dead end - show the
       ;; platform connect cards inline instead of a bare link (the same
       ;; cards as the social-account-connect modal).
       [:div
        {:class ["space-y-4"]}
        [:p
         {:class ["text-sm" "text-gray-600"]}
         "Connect the account you want to post to:"]
        (connect-modal/platform-grid state)])]))

(defn- instagram-highlight-selector
  [selected user-id highlights]
  (when (seq highlights)
    (let [entry (some #(when (= (:instagram.user/id %) user-id) %) selected)]
      [:div
       {:class ["mt-1" "pl-3" "border-l-2" "border-gray-200"]}
       [:div {:class ["text-xs" "text-gray-500" "mb-1"]} "Highlight:"]
       [:select
        {:class ["w-full" "text-xs" "border" "border-gray-300" "rounded" "px-2" "py-1" "bg-white"]
         :value (or (:highlight-id entry) "")
         :on {:change [[:social-scheduling/set-highlight-id
                        user-id
                        :event/target.value]]}}
        [:option {:value ""} "-"]
        (map (fn [h]
               ;; `:value` on the select is set before the options are
               ;; mounted, which the browser ignores. Mark the chosen
               ;; option as `:selected` so a freshly created select
               ;; displays the highlight that will actually be posted.
               [:option {:value (:highlight/id h)
                         :selected (= (:highlight/id h) (:highlight-id entry))
                         :replicant/key (:highlight/id h)}
                (:highlight/title h)])
             highlights)]])))

(defn- facebook-crosspost-section
  [selected-destinations user-id facebook-linkages]
  (let [entry (some #(when (and (= :instagram (:destination/kind %))
                                (= (:instagram.user/id %) user-id))
                       %)
                    selected-destinations)]
    (when (seq facebook-linkages)
      [:div
       {:class ["mt-1" "pl-3" "border-l-2" "border-gray-200"]}
       [:div {:class ["text-xs" "text-gray-500" "mb-1"]} "Share to Facebook:"]
       [:div
        {:class ["space-y-1"]}
        (map
          (fn [linkage]
            (let [dest-id (str (:facebook-linkage/id linkage))
                  checked (and (:share-to-fb? entry)
                               (= (:facebook-linkage/destination entry) dest-id))]
              [:label
               {:class ["flex" "items-center" "gap-2" "text-xs" "text-gray-700"
                        "cursor-pointer" "py-0.5"]
                :replicant/key dest-id}
               [:input
                {:type "checkbox"
                 :checked checked
                 :class ["h-3.5" "w-3.5" "text-indigo-600" "border-gray-300"
                         "rounded" "cursor-pointer"]
                 :on {:click [[:social-scheduling/toggle-fb-destination
                               user-id linkage]]}}]
               [:span (:facebook-linkage/name linkage)]])
            )
          facebook-linkages)]])))

(defn- checkbox
  "A checkbox that toggles `store-key` in the store, or the nested
   `store-path`. With `remember?` the next post starts from the
   choice, see `remembered-store-checks`."
  [{:keys [label description checked disabled store-key store-path remember?]}]
  [:label
   {:class (into ["flex" "items-start" "gap-3" "py-1.5" "text-sm" "cursor-pointer"]
                 (when disabled ["opacity-50" "cursor-not-allowed"]))}
   [:input {:type "checkbox"
            :checked (boolean checked)
            :disabled disabled
            :class ["mt-0.5" "h-4" "w-4" "rounded" "border-gray-300" "text-gray-900"]
            :on (when-not disabled
                  {:click (cond-> [(if store-path
                                     [:store/assoc-in store-path (not checked)]
                                     [:store/assoc store-key (not checked)])]
                            remember? (conj [:social-scheduling/remember-options]))})}]
   [:span
    [:span {:class ["block" "font-medium" "text-gray-800"]} label]
    (when description
      [:span {:class ["block" "text-xs" "text-gray-500"]} description])]])

(defn- tiktok-reconnect-note
  "Shown when `:query/tiktok-creator-info` answered with
   `:tiktok/creator-info-error`: TikTok no longer accepts the account's
   refresh token, so nothing can be published until it is reconnected."
  []
  [:p {:class ["text-xs" "text-red-600"]}
   "Storrito can no longer access this TikTok account. Please "
   [:a {:href "/ui/social-accounts"
        :class ["underline"]}
    "reconnect your TikTok account"]
   " and try again."])

(defn- tiktok-options
  [state result creator-info]
  (let [image-post? (:story-media/image-post? result)
        data (:tiktok/creator-info creator-info)
        privacy-levels (:privacy_level_options data)
        privacy-level (selected-tiktok-privacy-level state privacy-levels)]
    [:div
     {:class ["space-y-4" "border-t" "border-gray-100" "pt-3"]}
     ;; bundle.social has no photo title, only accounts of Storrito's
     ;; own TikTok integration get one.
     (when (and image-post? (seq (selected-tiktok-ids state)))
       [:div
        {:class ["space-y-2"]}
        [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "Photo title"]
        [:input {:type "text"
                 :value (or (::tiktok-title state) "")
                 :maxLength 90
                 :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                         "rounded-lg" "focus:ring-2" "focus:ring-gray-500" "focus:border-gray-500"]
                 :on {:input [[:store/assoc ::tiktok-title :event/target.value]]}}]
        [:p {:class ["text-xs" "text-gray-500"]}
         (str (count (or (::tiktok-title state) "")) "/90 characters")]])

     (when image-post?
       [:div
        {:class ["space-y-2"]}
        [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "Photo description"]
        [:textarea {:value (or (::tiktok-description state) (caption state))
                    :rows 3
                    :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                            "rounded-lg" "focus:ring-2" "focus:ring-gray-500"
                            "focus:border-gray-500" "resize-none"]
                    :on {:input [[:store/assoc ::tiktok-description :event/target.value]]}}]])

     (if (seq privacy-levels)
       [:div
        {:class ["space-y-2"]}
        [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "Who can view this post"]
        [:select {:required true
                  :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                          "rounded-lg" "focus:ring-2" "focus:ring-gray-500" "focus:border-gray-500"]
                  :on {:change [[:store/assoc ::tiktok-privacy-level :event/target.value]]}}
         [:option {:value ""
                   :disabled true
                   :selected (nil? privacy-level)
                   :replicant/key "__placeholder"}
          "Choose who can view this post"]
         (map (fn [level]
                [:option {:value level
                          :selected (= level privacy-level)
                          :replicant/key level}
                 (get privacy-level-labels level level)])
              privacy-levels)]]
       (if (:tiktok/creator-info-error creator-info)
         (tiktok-reconnect-note)
         [:p {:class ["text-xs" "text-gray-500"]}
          "TikTok creator settings are loading. Please choose a privacy level once the options are available."]))

     ;; The levels of the bundle.social accounts, while an own account
     ;; needs a reconnect.
     (when (and (seq privacy-levels)
                (:tiktok/creator-info-error creator-info))
       (tiktok-reconnect-note))

     [:div
      {:class ["grid" "gap-2" "sm:grid-cols-2"]}
      (when image-post?
        (checkbox {:label "Auto Audio"
                   :description "Select a random audio track"
                   :checked (::tiktok-auto-audio state)
                   :store-key ::tiktok-auto-audio}))
      (checkbox {:label "AI Generated"
                 :description "Label as AI-generated content"
                 :checked (::tiktok-ai-generated state)
                 :store-key ::tiktok-ai-generated})
      (checkbox {:label "Partnership"
                 :description "Paid partnership to promote a third-party business"
                 :checked (::tiktok-branded-content state)
                 :store-key ::tiktok-branded-content})
      (checkbox {:label "Branded Content"
                 :description "Promoting the creator's own business"
                 :checked (::tiktok-brand-organic state)
                 :store-key ::tiktok-brand-organic})]

     (when data
       [:div
        {:class ["grid" "gap-2" "sm:grid-cols-3" "border-t" "border-gray-100" "pt-3"]}
        (checkbox {:label "Disable comments"
                   :description "Disable comment function"
                   :checked (::tiktok-disable-comment state)
                   :disabled (:comment_disabled data)
                   :store-key ::tiktok-disable-comment})
        (when-not image-post?
          (checkbox {:label "Disable Duet"
                     :description "Prevent Duets"
                     :checked (::tiktok-disable-duet state)
                     :disabled (:duet_disabled data)
                     :store-key ::tiktok-disable-duet}))
        (when-not image-post?
          (checkbox {:label "Disable Stitch"
                     :description "Prevent Stitches"
                     :checked (::tiktok-disable-stitch state)
                     :disabled (:stitch_disabled data)
                     :store-key ::tiktok-disable-stitch}))])]))

(defn- story-first-comment-field
  [state]
  [:div
   {:class ["space-y-2"]}
   [:label
    {:class ["flex" "items-center" "gap-2" "text-sm" "font-medium" "text-gray-700"]}
    "First comment"
    [:span
     {:class ["px-1" "py-px" "rounded" "text-[10px]" "font-bold"
              "uppercase" "leading-none" "bg-yellow-400" "text-gray-900"]}
     "New"]]
   [:p {:class ["text-xs" "text-gray-500"]}
    "Optional. Posted as a comment on the story right after it goes live."]
   [:textarea {:value (or (::story-first-comment state) "")
               :placeholder "Add a comment or #hashtags ..."
               :rows 3
               :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                       "rounded-lg" "focus:ring-2" "focus:ring-pink-500"
                       "focus:border-pink-500" "resize-none"]
               :on {:input [[:store/assoc ::story-first-comment :event/target.value]]}}]])

(defn- instagram-account-sync-hint
  "Replaces the Facebook and Highlights options while the stored account
   data has neither. A freshly connected account stays like this until a
   sync fetches its linked Facebook pages and highlight albums from
   Instagram, which so far required a detour to the Instagram Accounts
   panel (brain-7)."
  [state user-id]
  (let [syncing? (contains? (::syncing-user-ids state) user-id)
        synced? (contains? (::synced-user-ids state) user-id)]
    [:div
     {:class ["mt-1" "flex" "flex-wrap" "items-center" "justify-between" "gap-2"]}
     [:p {:class ["text-xs" "text-gray-500"]}
      (if synced?
        (str "Still no Facebook page or highlight album found. Link a Facebook "
             "page to this account in the Instagram app or create a highlight, "
             "then sync again.")
        "No Facebook page or highlight album found yet. Sync to fetch them from Instagram.")]
     [:button
      {:type "button"
       :disabled syncing?
       :class (into ["shrink-0" "inline-flex" "items-center" "rounded-lg" "border"
                     "px-3" "py-1" "text-xs" "font-semibold"]
                    (if syncing?
                      ["border-gray-200" "bg-gray-100" "text-gray-400" "cursor-not-allowed"]
                      ["border-pink-200" "bg-white" "text-pink-700" "hover:bg-pink-50"
                       "cursor-pointer"]))
       :on {:click [[:social-scheduling/sync-instagram-account user-id]]}}
      (if syncing? "Syncing..." "Sync")]]))

(defn- instagram-destination-settings-section
  [state result]
  (let [selected (selected-instagram-accounts state)
        all-selected (selected-destinations state)
        users (:instagram/users result)
        rows (keep (fn [entry]
                     (let [user-id (:instagram.user/id entry)
                           user (some #(when (= (:instagram.user/id %) user-id) %) users)
                           fb-linkages (:instagram.user/facebook-linkages user)
                           highlights-query (instagram-highlights-query user-id)
                           highlights (:instagram.user/highlights
                                       (query/get-result state highlights-query))
                           options? (or (seq fb-linkages) (seq highlights))]
                       ;; The sync hint waits for the highlights query, so an
                       ;; account is not reported as empty while it loads.
                       (when (or options? (query/available? state highlights-query))
                         [:div
                          {:class ["p-2.5" "rounded-lg" "border" "border-gray-100"
                                   "bg-gray-50"]
                           :replicant/key (str "story-settings-" user-id)}
                          [:div
                           {:class ["text-xs" "font-medium" "text-gray-600" "mb-1"]}
                           (str "@" (:instagram.user/name user))]
                          (when (seq fb-linkages)
                            (facebook-crosspost-section all-selected user-id fb-linkages))
                          (when (seq highlights)
                            (instagram-highlight-selector selected user-id highlights))
                          (when-not options?
                            (instagram-account-sync-hint state user-id))])))
                   selected)]
    (when (seq selected)
      [:div
       {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-3"]}
       [:h3 {:class ["flex" "items-center" "gap-2" "text-sm" "font-semibold" "text-gray-900"]}
        [:span {:class ["text-pink-500"]}
         (icons/instagram-logo ["h-4" "w-4"])]
        "Instagram Story options"]
       (story-first-comment-field state)
       (checkbox {:label "AI-generated content"
                  :description "Label the story as AI-generated on Instagram"
                  :checked (::story-ai-generated state)
                  :store-key ::story-ai-generated})
       rows])))

(defn- reel-cover-upload-section
  [state]
  (let [uploading? (::reels-cover-uploading? state)
        cover-name (::reels-cover-name state)
        error (::reels-cover-upload-error state)]
    [:div
     {:class ["rounded-lg" "border" "border-gray-100" "bg-gray-50" "p-3" "space-y-2"]}
     [:div
      {:class ["flex" "items-start" "justify-between" "gap-3"]}
      [:div
       [:div {:class ["text-sm" "font-medium" "text-gray-800"]}
        "Cover photo"]
       [:p {:class ["mt-0.5" "text-xs" "text-gray-500"]}
        "Optional. Upload a JPG or PNG cover image for the Reel."]]
      [:label
       {:class (into ["shrink-0" "inline-flex" "items-center" "justify-center" "rounded-lg"
                      "px-3" "py-1.5" "text-xs" "font-semibold" "border"]
                     (if uploading?
                       ["border-gray-200" "bg-gray-100" "text-gray-400" "cursor-not-allowed"]
                       ["border-fuchsia-200" "bg-white" "text-fuchsia-700" "hover:bg-fuchsia-50"
                        "cursor-pointer"]))}
       (if uploading? "Uploading..." "Upload cover")
       [:input {:type "file"
                :accept "image/jpeg,image/png"
                :disabled uploading?
                :class ["hidden"]
                :on {:change [[:social-scheduling/upload-reel-cover :event/target.files]]}}]]]
     (when cover-name
       [:div
        {:class ["flex" "items-center" "justify-between" "gap-3" "rounded" "bg-white" "px-2.5"
                 "py-1.5" "text-xs"]}
        [:span {:class ["truncate" "text-gray-700"]}
         cover-name]
        [:button
         {:type "button"
          :class ["shrink-0" "font-medium" "text-red-600" "hover:text-red-700" "cursor-pointer"]
          :on {:click [[:store/dissoc ::reels-cover-blob-id]
                       [:store/dissoc ::reels-cover-name]
                       [:store/dissoc ::reels-cover-upload-error]]}}
         "Remove"]])
     (when error
       [:p {:class ["text-xs" "text-red-600"]} error])]))

(defn- reels-options-section
  [state result]
  (when (seq (selected-reels-ids state))
    [:div
     {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-4"]}
     [:div
      [:h3 {:class ["flex" "items-center" "gap-2" "text-sm" "font-semibold" "text-gray-900"]}
       [:span {:class ["text-fuchsia-600"]}
        (icons/instagram-logo ["h-4" "w-4"])]
       "Instagram Reel options"]
      [:p {:class ["mt-1" "text-xs" "text-gray-500"]}
       "The caption / description field above is used as the Reel caption."]]
     (reel-cover-upload-section state)
     [:div
      {:class ["grid" "gap-2" "sm:grid-cols-2"]}
      (checkbox {:label "Recommend on Facebook"
                 :description "Let Instagram recommend the Reel on Facebook"
                 :checked (not= false (::reels-recommend-facebook state))
                 :store-key ::reels-recommend-facebook
                 :remember? true})
      (checkbox {:label "Show Reel in Instagram posts"
                 :description "Also show the Reel in your profile grid / posts"
                 :checked (::reels-recommend-feed state)
                 :store-key ::reels-recommend-feed
                 :remember? true})
      (checkbox {:label "AI-generated content"
                 :description "Label the Reel as AI-generated on Instagram"
                 :checked (::reels-ai-generated state)
                 :store-key ::reels-ai-generated})]
     (when (and (:story-media/reel-ready? result)
                (not (:story-media/video-url result)))
       [:p {:class ["rounded-lg" "border" "border-blue-200" "bg-blue-50" "px-3" "py-2"
                    "text-sm" "text-blue-800"]}
        "The video will be rendered automatically before posting."])]))

(def ^:private unrestricted-creator-info
  "The creator settings an account of bundle.social is offered: every
   privacy level, nothing disabled. TikTok's `creator_info` needs
   Storrito's own TikTok token, which such an account does not have."
  {:tiktok/creator-info
   {:privacy_level_options (into []
                                 (comp (filter #(= :privacy (:option/key %)))
                                       (mapcat :option/choices)
                                       (map :choice/value))
                                 (social-platforms/options "TIKTOK" "POST"))}})

(defn- tiktok-settings-section
  "The \"TikTok options\" card, for the accounts of Storrito's own
   TikTok integration and the ones of bundle.social. With an own account
   selected, its creator settings apply to both."
  [state result]
  (when (or (seq (selected-tiktok-ids state))
            (options-card-selected? state :tiktok))
    (let [account-id (first (selected-tiktok-ids state))
          creator-info-query (tiktok-creator-info-query account-id)
          own-creator-info (when creator-info-query
                             (query/get-result state creator-info-query))
          creator-info (cond
                         (nil? creator-info-query)
                         unrestricted-creator-info

                         ;; The own account needs a reconnect: the
                         ;; bundle.social accounts still get every level.
                         (and (:tiktok/creator-info-error own-creator-info)
                              (options-card-selected? state :tiktok))
                         (merge own-creator-info unrestricted-creator-info)

                         :else
                         own-creator-info)]
      [:div
       {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-4"]}
       [:h3 {:class ["flex" "items-center" "gap-2" "text-sm" "font-semibold" "text-gray-900"]}
        [:span {:class ["text-gray-900"]}
         (icons/tiktok-logo ["h-4" "w-4"])]
        "TikTok options"]
       (tiktok-options state result creator-info)])))

(defn- social-option-select
  [state kind {:option/keys [key label hint choices]}]
  (let [current (get (social-options state kind) key (:choice/value (first choices)))]
    [:div
     {:class ["space-y-2"]
      :replicant/key (str (name kind) "-" (name key))}
     [:label {:class ["text-sm" "font-medium" "text-gray-700"]} label]
     [:select {:class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                       "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                       "focus:border-indigo-500"]
               :on {:change [[:store/assoc-in [::social-options kind key] :event/target.value]
                             [:social-scheduling/remember-options]]}}
      ;; `:selected` on the option, not `:value` on the select: Replicant
      ;; sets a select's value before its options exist.
      (for [{:choice/keys [value] :as choice} choices]
        [:option {:value value
                  :selected (= value current)
                  :replicant/key value}
         (:choice/label choice)])]
     (when hint
       [:p {:class ["text-xs" "text-gray-500"]} hint])]))

(defn- social-option-input
  "A text input of an option, with its label and hint; `value` is the
   chosen value or the prefill."
  [kind {:option/keys [key label hint]} input-attrs value]
  [:div
   {:class ["space-y-2"]
    :replicant/key (str (name kind) "-" (name key))}
   [:label {:class ["text-sm" "font-medium" "text-gray-700"]} label]
   [:input (merge {:value value
                   :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                           "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                           "focus:border-indigo-500"]
                   :on {:input [[:store/assoc-in [::social-options kind key] :event/target.value]]}}
                  input-attrs)]
   (when hint
     [:p {:class ["text-xs" "text-gray-500"]} hint])])

(defn- social-option-url
  [state kind {:option/keys [key] :as option}]
  (social-option-input kind option
                       {:type "url" :placeholder "https://"}
                       (get (social-options state kind) key "")))

(defn- social-option-text
  [state result kind {:option/keys [key max-length] :as option}]
  (let [default (social-platforms/option-default option (:story-media/name result))]
    (social-option-input kind option
                         {:type "text" :placeholder default :maxLength max-length}
                         (get (social-options state kind) key (or default "")))))

(defn- social-option-checkbox
  [state kind {:option/keys [key label hint]}]
  [:div {:replicant/key (str (name kind) "-" (name key))}
   (checkbox {:label label
              :description hint
              :checked (true? (get (social-options state kind) key))
              :store-path [::social-options kind key]})])

(defn- social-option
  [state result kind option]
  (case (:option/type option)
    :checkbox (social-option-checkbox state kind option)
    :text (social-option-text state result kind option)
    :url (social-option-url state kind option)
    (social-option-select state kind option)))

(defn- social-options-section
  "The `:post-type/options` of every selected bundle.social post type,
   e.g. LinkedIn's visibility and Pinterest's title, link and AI label."
  [state result]
  (for [kind (selected-platforms state)
        :let [post-type (social-platforms/post-type-by-kind kind)
              platform (social-platforms/by-id (:social-platform/id post-type))]
        :when (and (seq (:post-type/options post-type))
                   (not (:post-type/options-card post-type)))]
    [:div
     {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-4"]
      :replicant/key (str "social-options-" (name kind))}
     [:h3 {:class ["flex" "items-center" "gap-2" "text-sm" "font-semibold" "text-gray-900"]}
      [:span {:class (:social-platform/text-class platform)}
       ((:social-platform/icon platform) ["h-4" "w-4"])]
      (str (:post-type/group-label post-type) " options")]
     (for [option (:post-type/options post-type)]
       (social-option state result kind option))]))

(defn- youtube-settings-section
  [state result]
  (when (or (seq (selected-youtube-ids state))
            (options-card-selected? state :youtube))
    [:div
     {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-4"]}
     [:h3 {:class ["flex" "items-center" "gap-2" "text-sm" "font-semibold" "text-gray-900"]}
      [:span {:class ["text-red-600"]}
       (icons/youtube-logo ["h-4" "w-4"])]
      "YouTube options"]
     [:div
      {:class ["space-y-2"]}
      [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "YouTube title"]
      [:input {:type "text"
               :value (youtube-title state result)
               :maxLength 100
               :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                       "rounded-lg" "focus:ring-2" "focus:ring-red-500" "focus:border-red-500"]
               :on {:input [[:store/assoc ::youtube-title :event/target.value]]}}]]
     [:div
      {:class ["space-y-2"]}
      [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "Visibility"]
      ;; `:selected` on the option, not `:value` on the select: Replicant
      ;; sets a select's value before its options exist.
      [:select {:class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                        "rounded-lg" "focus:ring-2" "focus:ring-red-500" "focus:border-red-500"]
                :on {:change [[:store/assoc ::youtube-privacy-status :event/target.value]
                              [:social-scheduling/remember-options]]}}
       (map (fn [[value label]]
              [:option {:value value
                        :selected (= value (or (::youtube-privacy-status state) default-youtube-privacy))
                        :replicant/key value}
               label])
            youtube-privacy-choices)]]
     [:label
      {:class ["flex" "items-start" "gap-3" "text-sm" "text-gray-700" "cursor-pointer"]}
      [:input {:type "checkbox"
               :checked (boolean (::youtube-made-for-kids state))
               :class ["mt-0.5" "h-4" "w-4" "rounded" "border-gray-300" "text-red-600"]
               :on {:click [[:store/assoc ::youtube-made-for-kids
                             (not (::youtube-made-for-kids state))]
                            [:social-scheduling/remember-options]]}}]
      [:span "Made for kids"]]
     [:label
      {:class ["flex" "items-start" "gap-3" "text-sm" "text-gray-700" "cursor-pointer"]}
      [:input {:type "checkbox"
               :checked (boolean (::youtube-synthetic-media state))
               :class ["mt-0.5" "h-4" "w-4" "rounded" "border-gray-300" "text-red-600"]
               :on {:click [[:store/assoc ::youtube-synthetic-media
                             (not (::youtube-synthetic-media state))]]}}]
      [:span "Altered or synthetic media"]]]))

(defn- caption-usage-text
  "\"Used for Instagram Reel captions, TikTok descriptions, YouTube
   Short descriptions, and Facebook Reel captions.\", naming every
   launched post type that publishes the caption. Instagram, TikTok and
   YouTube are Storrito's own integrations, not bundle.social platforms
   of the catalog, so they are named here; they also keep the list at
   three entries or more, which the joining below relies on."
  []
  (let [uses (concat ["Instagram Reel captions" "TikTok descriptions" "YouTube Short descriptions"]
                     (for [post-type (social-platforms/post-types)
                           :when (and (:post-type/caption? post-type)
                                      ;; Named above with Storrito's own
                                      ;; integration it joins:
                                      (not (:post-type/destination-group post-type))
                                      (:social-platform/launched?
                                        (social-platforms/by-id (:social-platform/id post-type))))]
                       (str (:post-type/label post-type) " captions")))]
    (str "Used for " (str/join ", " (butlast uses)) ", and " (last uses) ".")))

(defn- own-caption-shortened
  "`[max-length label]` when the text exceeds the limit of the own
   integration `kind`, whose schedule command shortens it
   (`storrito.caption-limits`). YouTube's limit is in bytes; the note
   names it in characters, which only differs for texts full of umlauts
   or emoji."
  [state result kind]
  (let [photo? (:story-media/image-post? result)
        [text max-length length label]
        (case kind
          :youtube [(caption state) caption-limits/youtube-description-max-bytes text/utf8-length "YouTube"]
          :tiktok (if photo?
                    [(tiktok-description state) caption-limits/tiktok-photo-description-max-length count "TikTok"]
                    [(caption state) caption-limits/tiktok-caption-max-length count "TikTok"])
          nil)]
    (when (and max-length (< max-length (length text)))
      [max-length label])))

(defn- bundle-caption-shortened
  "`[max-length label]` when the text exceeds the limit of the
   bundle.social post type of `kind`
   (`app.bundle-social.platforms/fit-caption`)."
  [state result kind]
  (let [post-type (social-platforms/post-type-by-kind kind)]
    (when (social-platforms/caption-shortened? (:social-platform/id post-type)
                                               (:post-type/id post-type)
                                               (social-text state result kind))
      [(:post-type/max-text-length post-type) (:post-type/group-label post-type)])))

(defn caption-link-query
  "`:query/social-caption-link` for the text of `kind`, when its post type
   bills a post with a link as a service of its own (X) and the text is
   not blank. The link detection only runs on the server."
  [state result kind]
  (let [post-type (social-platforms/post-type-by-kind kind)
        text (when post-type
               (social-text state result kind))]
    (when (and (:post-type/link-service post-type)
               (not (str/blank? text)))
      {:query/kind :query/social-caption-link
       :query/data {:social-platform/id (:social-platform/id post-type)
                    :post-type/id (:post-type/id post-type)
                    :text text}})))

(defn caption-link-queries
  "`[kind query]` of every selected kind with a `caption-link-query`."
  [state result]
  (for [kind (selected-platforms state)
        :let [q (caption-link-query state result kind)]
        :when q]
    [kind q]))

(defn caption-link-note
  "The note on the links of the text of `kind`, from the server's answer
   to `caption-link-query`: the higher price of a post with a link (the
   organization's prices), and that the cut to the post type's limit
   drops a link. A link post that costs no more only gets the latter."
  [kind {:social-caption/keys [link-post? link-dropped? price link-price]}]
  (let [{:post-type/keys [label group-label max-text-length]}
        (social-platforms/post-type-by-kind kind)
        dropped (str "shortening the caption to " max-text-length
                     " characters for " group-label " cuts off a link.")]
    (cond
      ;; Not when the organization pays no more for a link (a price
      ;; adjustment).
      (and link-post? (< price link-price))
      (str "This caption contains a link, so each " label " costs "
           (text/format-credits link-price)
           " credits instead of " (text/format-credits price)
           ". Remove the link, including bare domains like example.com, to pay the regular price."
           (when link-dropped?
             (str " Also, " dropped)))

      link-dropped?
      (str "A link will not be posted: " dropped
           " Shorten the caption or move the link to the front to keep it."))))

(defn caption-link-answers
  "`::caption-links` after the answer to `q`, the `caption-link-query`
   of `kind` it was sent as; `current` is that query for the state now.
   The kept answer notes its `:social-caption/text`. A late answer for
   an older text is dropped, a failed request (an answer without
   `:social-caption/link-post?`) removes the previous answer, which may
   be wrong for this text."
  [caption-links kind q current answer]
  (cond
    (not= (get-in q [:query/data :text])
          (get-in current [:query/data :text]))
    caption-links

    (boolean? (:social-caption/link-post? answer))
    (assoc caption-links kind (assoc answer :social-caption/text (get-in q [:query/data :text])))

    :else
    (dissoc caption-links kind)))

(defn- caption-link-notes
  "`caption-link-note` per selected kind with a `caption-link-query`,
   from its latest answer (`::caption-links`, see
   `app.social-scheduling.register/query-caption-links!`). That answer
   can be for the text before the last keystrokes: the note stays while
   the new answer loads instead of flickering."
  [state result]
  (distinct
    (for [[kind _] (caption-link-queries state result)
          :let [note (some->> (get-in state [::caption-links kind])
                              (caption-link-note kind))]
          :when note]
      note)))

(defn- caption-shortened-notes
  "\"Shortened to 500 characters for Threads.\" per selected post type
   whose limit its text exceeds, the bundle.social ones and the own
   YouTube and TikTok integrations. Equal notes, e.g. of an own and a
   bundle.social YouTube channel, are shown once."
  [state result]
  (distinct
    (for [kind (selected-platforms state)
          :let [shortened (if (social-platforms/post-type-by-kind kind)
                            (bundle-caption-shortened state result kind)
                            (own-caption-shortened state result kind))]
          :when shortened
          :let [[max-length label] shortened]]
      (str "Shortened to " max-length " characters for " label "."))))

(defn- content-section
  "The caption is used by Reels, TikTok and YouTube posts, so the section is
   hidden when none of those platforms is selected (e.g. only Instagram
   Stories, which use their own first-comment field)."
  [state result]
  (when (some #(or (#{:reels :tiktok :youtube} %)
                  (:post-type/caption? (social-platforms/post-type-by-kind %)))
            (selected-platforms state))
    [:div
     {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-4"]}
     [:div
      {:class ["space-y-2"]}
      [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "Caption / description"]
      [:p {:class ["text-xs" "text-gray-500"]}
       (caption-usage-text)]
      [:textarea {:value (or (::caption state) "")
                  :placeholder "Add caption, description, or #hashtags ..."
                  :rows 4
                  :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                          "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                          "focus:border-indigo-500" "resize-none"]
                  :on {:input [[:store/assoc ::caption :event/target.value]
                               [:social-scheduling/query-caption-links]]}}]
      (for [note (concat (caption-shortened-notes state result)
                         (caption-link-notes state result))]
        [:p {:replicant/key note
             :class ["text-xs" "text-amber-700"]}
         note])]]))

(defn- command-key
  [cmd]
  (str (:command/kind cmd)
       "-"
       (or (get-in cmd [:command/data :instagram.user/id])
           (get-in cmd [:command/data :tiktok.user/id])
           (get-in cmd [:command/data :youtube.user/id])
           (some->> (get-in cmd [:command/data :social.account/uuid])
                    (str (get-in cmd [:command/data :post/type]) "-"))
           "default")))

(defn- status-label
  [status]
  (case status
    :executed "Live"
    :scheduled "Scheduled"
    :in-progress "In progress"
    :back-off "Retrying"
    :processing "Processing"
    :before-24h "Expired"
    :error "Failed"
    (name status)))

(defn- status-classes
  [status]
  (case status
    :executed ["bg-green-50" "text-green-700" "border-green-200"]
    :scheduled ["bg-blue-50" "text-blue-700" "border-blue-200"]
    :in-progress ["bg-amber-50" "text-amber-700" "border-amber-200"]
    :back-off ["bg-amber-50" "text-amber-700" "border-amber-200"]
    :processing ["bg-amber-50" "text-amber-700" "border-amber-200"]
    :before-24h ["bg-gray-50" "text-gray-700" "border-gray-200"]
    :error ["bg-red-50" "text-red-700" "border-red-200"]
    ["bg-gray-50" "text-gray-700" "border-gray-200"]))

(defn- activity-time-label
  [entry]
  (if-let [executed (:schedule/executed entry)]
    (if (and (= :instagram (:schedule/platform entry))
             (= :before-24h (:schedule/status entry)))
      (str "Posted " (f/format-standard-datetime executed) " (expired after 24h)")
      (str "Went live " (f/format-standard-datetime executed)))
    (when-let [date (:schedule/date entry)]
      (str "Scheduled for " (f/format-standard-datetime date)))))

(defn- activity-icon
  [platform classes]
  (if-let [social-platform (social-platforms/by-key platform)]
    ((:social-platform/icon social-platform) classes)
    (case platform
      :tiktok (icons/tiktok-logo classes)
      :youtube (icons/youtube-logo classes)
      (icons/instagram-logo classes))))

(defn- activity-icon-color
  [platform]
  (if-let [social-platform (social-platforms/by-key platform)]
    (:social-platform/text-class social-platform)
    (case platform
      :tiktok ["text-gray-900"]
      :youtube ["text-red-600"]
      :reels ["text-fuchsia-600"]
      ["text-pink-500"])))

(defn- ai-generated-badge
  []
  [:span
   {:data-tip "Labeled as AI-generated content"
    :aria-label "Labeled as AI-generated content"
    :class ["tooltip" "inline-flex" "items-center" "px-1" "py-px" "rounded"
            "border" "border-gray-300" "text-gray-500" "text-[10px]"
            "font-bold" "leading-none"]}
   "AI"])

(defn- duplicate-story-schedules
  [state result]
  (let [target-ms (target-date-ms state)
        selected-account-ids (set (map :instagram.user/id
                                       (selected-instagram-accounts state)))]
    (vec
      (filter (fn [entry]
                (let [entry-ms (date-value-ms (:schedule/date entry))]
                  (and (= :instagram (:schedule/platform entry))
                       (contains? selected-account-ids (:schedule/account-id entry))
                       entry-ms
                       (same-minute-ms? target-ms entry-ms))))
              (:social-scheduling/activity result)))))

(defn- duplicate-account-ids
  [existing skipped]
  (vec
    (distinct
      (concat
        (map :schedule/account-id existing)
        (map :instagram.user/id skipped)))))

(defn- skipped-duplicates
  [state cmds]
  (vec (mapcat #(get-in (command/get-latest-result state %)
                        [:story-publish/skipped-duplicates])
               cmds)))

(defn- skipped-duplicates-message
  [result account-ids]
  (let [name-by-id (into {}
                         (map (juxt :instagram.user/id :instagram.user/name))
                         (:instagram/users result))
        accounts (->> account-ids
                      (map #(str "@" (or (get name-by-id %) %)))
                      (str/join ", "))]
    (str "This story is already scheduled for "
         accounts
         " in the selected minute. No duplicate will be created.")))

(defn- activity-cancel-command
  "Command to cancel a scheduled activity entry. Only entries that are
   not yet posted and not currently in-flight can be canceled. Instagram
   stories also stay cancelable while they wait for a retry or for the
   account's queue (`:schedule/cancelable?`)."
  [entry]
  (when (or (= :scheduled (:schedule/status entry))
            (:schedule/cancelable? entry))
    (if (social-platforms/by-key (:schedule/platform entry))
      {:command/kind :command/social-post-cancel
       :command/data {:social.post/uuid (:schedule/social-post-uuid entry)}}
      (when-let [kind (case (:schedule/platform entry)
                        :instagram :command/story-post-cancel
                        :reels :command/reel-cancel
                        :tiktok :command/tiktok-post-cancel
                        :youtube :command/youtube-short-cancel
                        nil)]
        {:command/kind kind
         :command/data {:db/id (:db/id entry)}}))))

(defn- activity-cancel-confirm-message
  [entry]
  (if-let [social-platform (social-platforms/by-key (:schedule/platform entry))]
    (str "Cancel this scheduled " (:social-platform/name social-platform) " post?")
    (case (:schedule/platform entry)
      :reels "Cancel this scheduled Reel?"
      :tiktok "Cancel this scheduled TikTok post?"
      :youtube "Cancel this scheduled YouTube Short?"
      "Cancel this scheduled story?")))

(defn- scheduling-activity-section
  [state result]
  (let [activity (:social-scheduling/activity result)
        highlighted-eids (set (map :db/id (duplicate-story-schedules state result)))]
    (when (seq activity)
      [:div
       {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-3"]}
       [:div
        [:h3 {:class ["text-sm" "font-semibold" "text-gray-900"]}
         "Scheduling activity"]
        [:p {:class ["mt-1" "text-xs" "text-gray-500"]}
         "Where this media is scheduled, live, or previously posted."]]
       [:div
        {:class ["divide-y" "divide-gray-100"]}
        (for [entry activity]
          (let [status (:schedule/status entry)]
            [:div
             {:class (into ["py-2.5" "flex" "items-start" "justify-between" "gap-3"]
                           (when (contains? highlighted-eids (:db/id entry))
                             ["rounded-lg" "bg-amber-50" "px-2" "ring-1"
                              "ring-amber-200" "animate-pulse"]))
              :replicant/key (str (name (:schedule/platform entry)) "-" (:db/id entry))}
             [:div {:class ["min-w-0" "flex" "items-start" "gap-2"]}
              [:span
               {:class (into ["mt-0.5" "inline-flex" "h-5" "w-5" "shrink-0"
                              "items-center" "justify-center"]
                             (activity-icon-color (:schedule/platform entry)))}
               (activity-icon (:schedule/platform entry) ["h-4" "w-4"])]
              [:div {:class ["min-w-0"]}
               [:div {:class ["flex" "items-center" "gap-2" "flex-wrap"]}
                [:span {:class ["text-sm" "font-medium" "text-gray-900"]}
                 (:schedule/platform-label entry)]
                (when-let [account-name (:schedule/account-name entry)]
                  [:span {:class ["text-xs" "text-gray-500"]}
                   (str "@" account-name)])
                (when (:schedule/ai-generated? entry)
                  (ai-generated-badge))]
               (when-let [time-label (activity-time-label entry)]
                 [:div {:class ["mt-0.5" "text-xs" "text-gray-500"]}
                  time-label])
               (when-let [error (:schedule/error-message entry)]
                 [:div {:class ["mt-1" "text-xs" "text-red-600"]}
                  error])]]
             [:div {:class ["shrink-0" "flex" "flex-col" "items-end" "gap-1"]}
              [:span
               {:class (into ["rounded-full" "border" "px-2" "py-0.5"
                              "text-xs" "font-medium"]
                             (status-classes status))}
               (status-label status)]
              (when-let [cancel-command (activity-cancel-command entry)]
                (let [canceling? (command/issued? state cancel-command)
                      canceled? (command/success? state cancel-command)]
                  [:button
                   {:type "button"
                    :class ["inline-flex" "items-center" "gap-1" "text-xs" "font-medium"
                            "text-red-600" "hover:text-red-700" "hover:underline"
                            "cursor-pointer" "disabled:cursor-wait" "disabled:opacity-70"
                            "disabled:no-underline"]
                    :disabled (or canceling? canceled?)
                    :on {:click [[:js/confirm (activity-cancel-confirm-message entry)]
                                 [:data/command
                                  cancel-command
                                  {:on-success [[:social-scheduling/query
                                                 (social-scheduling-query state)]]
                                   :on-error [[:ui/show-error]]}]]}}
                   ;; bundle.social answers in up to ~2 s, then the page
                   ;; reloads; "Canceled" stays until the entry updates.
                   (when canceling?
                     [:span {:class ["h-3" "w-3" "animate-spin" "rounded-full" "border-2"
                                     "border-red-200" "border-t-red-600"]}])
                   (cond
                     canceling? "Canceling..."
                     canceled? "Canceled"
                     :else "Cancel")]))]]))]])))

(defn submission
  "Everything needed to decide whether and what to submit. Used to render
   the schedule-section and re-computed from the current state when the
   submit action is dispatched, so a click can never send a command
   payload that was rendered before the latest destination change."
  [state result]
  (let [selected (selected-destinations state)
        errors (vec (selected-errors state result))
        cmds (commands state result)
        skipped (skipped-duplicates state cmds)
        existing-duplicates (duplicate-story-schedules state result)
        duplicate-account-ids (duplicate-account-ids (when-not (::submitted? state)
                                                       existing-duplicates)
                                                     skipped)
        duplicate? (boolean (seq duplicate-account-ids))
        pending-cmds (vec (remove #(command/success? state %) cmds))
        date-error (schedule-date-error state)
        submitting? (::submitting? state)
        issued? (boolean (or submitting?
                             (some #(command/issued? state %) pending-cmds)))]
    {:selected selected
     :errors errors
     :cmds cmds
     :duplicate-account-ids duplicate-account-ids
     :duplicate? duplicate?
     :pending-cmds pending-cmds
     :date-error date-error
     :issued? issued?
     :can-submit? (boolean (and (seq selected)
                                (empty? errors)
                                (not date-error)
                                (not duplicate?)
                                (seq pending-cmds)
                                (not issued?)))}))

(defn- schedule-section
  [state result]
  (let [q (social-scheduling-query state)
        {:keys [selected errors cmds duplicate-account-ids duplicate?
                date-error issued? can-submit?]} (submission state result)]
    [:div
     {:class ["rounded-xl" "border" "border-gray-200" "bg-white" "p-4" "space-y-4"]}
     [:div
      {:class ["space-y-2"]}
      [:label {:class ["text-sm" "font-medium" "text-gray-700"]} "Date & time"]
      [:input {:type "datetime-local"
               :value (or (::schedule-date-str state)
                          (datetime-local/current-datetime-local))
               :min (datetime-local/current-datetime-local)
               :class (into ["w-full" "px-3" "py-2" "text-sm" "border"
                             "rounded-lg" "focus:ring-2" "focus:ring-indigo-500"
                             "focus:border-indigo-500"]
                            (if date-error
                              ["border-red-300" "bg-red-50"]
                              ["border-gray-300"]))
               :on {:input [[:store/assoc ::schedule-date-str :event/target.value]
                            [:store/assoc ::submitted? nil]]}}]
      (if date-error
        [:p {:class ["text-xs" "text-red-600"]}
         date-error]
        [:p {:class ["text-xs" "text-gray-500"]}
         "Choose now or a future date. Existing platform-specific pages remain available for advanced options."])]

     (when (seq errors)
       [:div
        {:class ["space-y-2"]}
        (map
          (fn [{:keys [platform error]}]
            [:div
             {:class ["rounded-lg" "border" "border-amber-200" "bg-amber-50" "px-3" "py-2"
                      "text-sm" "text-amber-800"]
              :replicant/key (name platform)}
             [:span {:class ["font-medium"]} (platform-title platform)]
             ": " error])
          errors)])

     (when-let [rendering (seq (selected-rendering-destinations state result))]
       [:div
        {:class ["rounded-lg" "border" "border-amber-200" "bg-amber-50" "px-3" "py-2"
                 "text-sm" "text-amber-800"]}
        [:span {:class ["font-medium"]}
         (str/join ", " (distinct (map :destination/label rendering)))]
        ": " (rendering-warning result)])

     (when-let [sticker-warnings (seq (selected-sticker-warnings state result))]
       [:div
        {:class ["space-y-2"]}
        (map
          (fn [{:instagram.user/keys [id name] :keys [warning]}]
            [:div
             {:class ["rounded-lg" "border" "border-amber-200" "bg-amber-50" "px-3" "py-2"
                      "text-sm" "text-amber-800"]
              :replicant/key (str "sticker-warning-" id)}
             [:span {:class ["font-medium"]} (str "@" name)]
             ": " warning])
          sticker-warnings)])

     (when (seq duplicate-account-ids)
       [:div
        {:class ["rounded-lg" "border" "border-amber-200" "bg-amber-50" "px-3" "py-2"]}
        [:p {:class ["text-sm" "font-medium" "text-amber-800"]}
         (skipped-duplicates-message result duplicate-account-ids)]])

     (when (and (::submitted? state)
                (empty? duplicate-account-ids))
       [:div
        {:class ["rounded-lg" "border" "border-green-200" "bg-green-50" "px-3" "py-2"]}
        [:p {:class ["text-sm" "font-medium" "text-green-800"]}
         "Scheduling commands were sent. Check each platform history for final status."]])

     (when (seq cmds)
       [:div
        {:class ["space-y-2"]}
        (map
          (fn [cmd]
            (when (command/error? state cmd)
              [:div
               {:class ["rounded-lg" "border" "border-red-200" "bg-red-50" "px-3" "py-2"
                        "text-sm" "text-red-800"]
                :replicant/key (command-key cmd)}
               (or (command/get-latest-error state cmd)
                   "Scheduling failed.")]))
          cmds)])

     [:button
      {:class (into ["w-full" "py-2.5" "px-4" "text-sm" "font-semibold" "rounded-lg"
                     "transition-colors" "inline-flex" "items-center" "justify-center" "gap-2"]
                    (if can-submit?
                      ["bg-gray-900" "text-white" "hover:bg-gray-800" "cursor-pointer"]
                      ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
       :disabled (not can-submit?)
       :on {:click (when can-submit?
                     ;; The commands are intentionally not passed along:
                     ;; submit! re-computes them from the current state, so
                     ;; a click right after a destination change cannot
                     ;; submit the account list of an older render (which
                     ;; posted stories to a de-selected account in the
                     ;; past):
                     [[:social-scheduling/submit q]])}}
      (submit-button-label state selected issued? duplicate?)]]))

(defn- platform-links
  [result]
  (let [eid (:story-media/eid result)]
    [:div
     {:class ["text-xs" "text-gray-500" "flex" "flex-wrap" "gap-3"]}
     [:span "Advanced pages:"]
     [:a {:href (str "/ui/story-publish?eid=" eid)
          :class ["text-indigo-600" "hover:text-indigo-700"]}
      "Instagram"]
     [:a {:href (str "/ui/reels-publish?story-eid=" eid)
          :class ["text-pink-600" "hover:text-pink-700"]}
      "Reels"]
     [:a {:href (str "/ui/tiktok-publish?story-eid=" eid)
          :class ["text-gray-700" "hover:text-gray-900"]}
      "TikTok"]
     [:a {:href (str "/ui/youtube-publish?story-eid=" eid)
          :class ["text-red-600" "hover:text-red-700"]}
      "YouTube"]]))

(defn render
  [state]
  (let [q (social-scheduling-query state)
        result (when q (query/get-result state q))
        loading (or (not q)
                    (and (query/loading? state q)
                         (not (query/available? state q))))]
    (if loading
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]
      (app-frame/app-frame
        state
        [:div
         {:class ["max-w-5xl" "mx-auto" "overflow-x-hidden"]}
         [:div
          {:class ["mb-4"]}
          [:h1
           {:class ["text-xl" "font-bold" "text-gray-900"]}
           "Schedule social post"]
          [:p
           {:class ["text-sm" "text-gray-500" "mt-1"]}
           (or (:story-media/name result) "Choose where this media should be scheduled.")]]
         [:div
          {:class ["flex" "flex-col" "lg:flex-row" "gap-6"]}
          (preview-section state result)
          [:div
           {:class ["flex-1" "space-y-5"]}
           (social-account-selector state result)
           (content-section state result)
           (instagram-destination-settings-section state result)
           (reels-options-section state result)
           (tiktok-settings-section state result)
           (youtube-settings-section state result)
           (social-options-section state result)
           (schedule-section state result)
           (scheduling-activity-section state result)
           (platform-links result)]]]))))

(defn on-load
  [location]
  (let [eid-str (or (get-in location [:location/query-params :story-eid])
                    (get-in location [:location/query-params :eid]))
        eid (when eid-str
              (let [n (js/parseInt eid-str 10)]
                (when-not (js/isNaN n)
                  n)))]
    (when eid
      [[:ui/hide-sidebar-on-mobile]
       ;; Authorize URLs and post limits for the inline platform
       ;; connect cards shown when no social account is connected yet:
       [:data/query connect-modal/tiktok-query]
       [:data/query connect-modal/youtube-query]
       [:data/query social-accounts-page/post-limits-query]
       [:store/assoc ::story-media-eid eid]
       [:store/assoc ::selected-destinations []]
       [:social-scheduling/restore-selected-destinations]
       [:store/assoc ::reels-recommend-facebook true]
       [:store/assoc ::reels-recommend-feed false]
       [:store/assoc ::reels-ai-generated nil]
       [:store/assoc ::story-ai-generated nil]
       [:store/assoc ::tiktok-ai-generated nil]
       ;; Per story: the text and checkbox options of bundle.social
       ;; (Pinterest's title, link and AI label) start from their
       ;; defaults, so a ticked AI label or a title never carries over;
       ;; choice options like LinkedIn's visibility come back with
       ;; `:social-scheduling/restore-options` below.
       [:store/dissoc ::social-options]
       [:store/dissoc ::syncing-user-ids]
       [:store/dissoc ::synced-user-ids]
       [:store/dissoc ::reels-cover-blob-id]
       [:store/dissoc ::reels-cover-name]
       [:store/dissoc ::reels-cover-uploading?]
       [:store/dissoc ::reels-cover-upload-error]
       [:store/assoc ::tiktok-video-cover-time-seconds "1"]
       [:store/assoc ::tiktok-privacy-level nil]
       ;; The rest of the TikTok card starts empty per story, like the
       ;; bundle.social options.
       [:store/dissoc ::tiktok-title]
       [:store/dissoc ::tiktok-description]
       [:store/dissoc ::tiktok-disable-comment]
       [:store/dissoc ::tiktok-disable-duet]
       [:store/dissoc ::tiktok-disable-stitch]
       [:store/dissoc ::tiktok-auto-audio]
       [:store/dissoc ::tiktok-branded-content]
       [:store/dissoc ::tiktok-brand-organic]
       [:store/dissoc ::youtube-title]
       [:store/assoc ::youtube-privacy-status default-youtube-privacy]
       [:store/assoc ::youtube-made-for-kids nil]
       [:store/assoc ::youtube-synthetic-media nil]
       [:store/assoc ::submitted? nil]
       [:store/assoc ::submitting? nil]
       [:store/assoc ::schedule-date-str nil]
       ;; After the defaults above, which it overrides:
       [:social-scheduling/restore-options]
       [:social-scheduling/query (social-scheduling-query {::story-media-eid eid})]
       [:db-view/start-listening
        #{:pages/social-scheduling}]])))

(def page
  {:page-id :pages/social-scheduling
   :route [["ui" "social-schedule"]]
   :on-load on-load
   :render #'render})
